import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authClient } from '../../utils/amplifyClient';
import '../../css/SingleFormContent.css';
import { useFormsContext } from '../../contexts/FormsContext';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { useUserContext } from '../../contexts/UserContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { tFormPrompts, tPrompt, tQuestionContent, tSectionContent } from '../../types';
import { createForm as createFormMutation, updateForm as updateFormMutation } from '../../graphql/mutations';
import { getForm as getFormQuery } from '../../graphql/queries';
import { listServiceItemsByCompanyId, listServiceProcedureConsumablesByCompanyId, listServiceProceduresByCompanyId, listVendorsByCompanyId } from '../../graphql/companyResourceQueries';
import { sortServiceItemsForListing, sortVendorsByName } from '../../utils/companyResourceUtils';
import { VendorModal } from '../company-resources/VendorModal';
import { ServiceProcedureModal } from '../company-resources/ServiceProcedureModal';
import type { CreateFormInput, UpdateFormInput } from '../../graphql/API';
import {
    DEFAULT_FORM_INPUT_TYPES,
    FORM_TYPES,
    INSPECTION_QUESTION_TYPE_OPTIONS,
    OPERATOR_RESPONSE_TYPE_OPTIONS,
    OPERATOR_RESPONSE_TYPES,
    FORM_ITEM_CATEGORY_OPTIONS,
    DAILY_SERVICE_ITEM_CATEGORY_OPTIONS,
    DAILY_SERVICE_OPERATOR_RESPONSE_TYPE_OPTIONS,
    FORM_ITEM_CATEGORIES,
    formTypeToQueryParam,
    formsListPath,
} from '../../utils/constants';
import { CustomModal } from '../CustomModal';
import {
    clearFormBuilderDraft,
    clearFormBuilderNewFormDrafts,
    formBuilderDraftKey,
    readFormBuilderDraft,
    writeFormBuilderDraft,
} from './formBuilderDraftStorage';

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PageHeader } from '../ui/headers';
import { BackButton, Badge, Btn, BtnIconOnly, Card, CardFooter, CollapsibleCard, SearchSuggest, Select } from '../ui';
import type { SelectOption } from '../ui';
import type { PMPartsAndProcedure } from '../../types/pmTypes';
import {
    catalogFieldsForPrompt,
    catalogFieldsFromPrompt,
    createCategoryStub,
    createOrFillCatalogItem,
    DS_SECTION_CATALOG,
    findCategoryCasing,
    findMatchingListing,
    findProcedureCategoryCasing,
    headingNamesForCatalog,
    itemsInCategory,
    pendingListingOverrides,
    resolveOrCreateCatalogListing,
    resolveVendorFromName,
    serviceItemOptionLabel,
    shouldStubServiceItemCategory,
    type DsSectionCatalogKind,
} from './formBuilderServiceCatalog';
import { IconActions, IconAngleDown, IconAngleUp, IconCopy, IconDragHandle, IconFlag, IconPlus, IconTrash } from '../ui/icons';
import { DragHandleIcon, SortableItem, SortablePrompt } from './FormBuilderShared';
import { FormCategory } from './FormCategory';
import { FormOperatorResponseType } from './FormOperatorResponseType';
import { UnitOfMeasureSearchSuggest } from '../pages-equipment/UnitOfMeasureSearchSuggest';
import { EquipmentCategoryFormOverlay } from '../pages-equipment/EquipmentCategoryFormOverlay';
import { ADD_EQUIPMENT_TYPE_OPTION_LABEL } from '../pages-equipment/equipmentConstants';
import {
    ADD_FORM_NAME_LABEL,
    ALL_EQUIPMENT_CATEGORY_VALUE,
    duplicateFormNameMessage,
    equipmentCategorySelectValue,
    equipmentCategoryStoredName,
    findDuplicateFormName,
    sortedEquipmentCategoryOptions,
    uniqueSortedFormNameOptions,
} from './formBuilderNameUtils';
import {
    applyProcedureToPrompt,
    createDailyServiceProcedurePrompt,
    isDailyServiceProcedurePrompt,
    procedureConsumablesHelperText,
    promptPatchForDailyServiceCategory,
    resolveSectionCatalogKind,
    sectionUsesCatalogSuggest,
    findProcedureForSuggestInput,
    isBoundProcedureSuggestText,
    sortedProcedureSuggestOptions,
    withResolvedCatalogKind,
} from './formBuilderProcedures';
import type { ServiceProcedure, ServiceProcedureConsumable } from '../../types/companyResourceTypes';

const FORM_INPUT_MODE_OPTIONS: SelectOption[] = [
    { value: 'text', label: 'Text' },
    { value: 'decimal', label: 'Number' },
    { value: 'textarea', label: 'Large Text' },
];

const inspectionResponseTypeOptions = (responseType: string): SelectOption[] => {
    const groups = ['Response Patterns', 'Custom Input'] as const;
    return groups.flatMap((groupLabel) => {
        const groupOptions: { value: string; label: string }[] = INSPECTION_QUESTION_TYPE_OPTIONS.filter(
            (opt) => opt.group === groupLabel,
        ).map((opt) => ({ value: opt.value, label: opt.label }));
        if (
            groupLabel === 'Response Patterns' &&
            (Object.values(OPERATOR_RESPONSE_TYPES) as string[]).includes(responseType) &&
            !INSPECTION_QUESTION_TYPE_OPTIONS.some((opt) => opt.value === responseType)
        ) {
            groupOptions.push({
                value: responseType,
                label: `${
                    OPERATOR_RESPONSE_TYPE_OPTIONS.find((option) => option.value === responseType)?.label ||
                    responseType
                } (legacy)`,
            });
        }
        return groupOptions.map((opt, index) => ({
            value: opt.value,
            label: opt.label,
            content: (
                <span className="form-response-type-option">
                    {index === 0 ? <span className="form-response-type-group">{groupLabel}</span> : null}
                    <span className="select-component-option-label">{opt.label}</span>
                </span>
            ),
        }));
    });
};

const getCategoryStatusTone = (
    categoryValue?: string | null,
): (typeof FORM_ITEM_CATEGORY_OPTIONS)[number]['statusTone'] | undefined => {
    if (categoryValue === FORM_ITEM_CATEGORIES.PROCEDURE) return 'success';
    return FORM_ITEM_CATEGORY_OPTIONS.find((c) => c.value === categoryValue)?.statusTone;
};

const getCategorySoftBadgeClass = (categoryValue?: string | null): string => {
    const tone = getCategoryStatusTone(categoryValue);
    return tone ? `badge-${tone}` : 'badge-neutral';
};

const getCategoryItemCardClass = (categoryValue?: string | null): string => {
    const tone = getCategoryStatusTone(categoryValue);
    return tone ? `item-card-${tone}` : '';
};

const serializeFormContents = (contents: tFormPrompts[]) =>
    JSON.stringify(
        contents.map((content) => {
            const { clientId: _sectionClientId, ...section } = content;
            if (!section.prompts?.length) return section;
            return {
                ...section,
                prompts: section.prompts.map(({ clientId: _clientId, ...prompt }) => prompt),
            };
        }),
    );

export type FormBuilderSavedForm = {
    id: string;
    name: string;
};

/** Mutation responses can return a stale/null status even when the write succeeded. */
type FormStatusCheck =
    | { result: 'matched' }
    | { result: 'mismatch'; observedStatus: string }
    | { result: 'unconfirmed' };

async function confirmSavedFormStatus(
    formId: string,
    expectedStatus: 'draft' | 'published',
    returnedStatus?: string | null,
): Promise<FormStatusCheck> {
    if (returnedStatus === expectedStatus) {
        return { result: 'matched' };
    }

    try {
        const response = (await authClient.graphql({
            query: getFormQuery,
            variables: { id: formId },
        })) as {
            data?: { getForm?: { status?: string | null } | null };
            errors?: Array<{ message: string }>;
        };
        if (response.errors?.length) {
            console.warn('Could not re-fetch form status after save:', response.errors);
            return { result: 'unconfirmed' };
        }
        const confirmedStatus = response.data?.getForm?.status;
        if (confirmedStatus === expectedStatus) {
            return { result: 'matched' };
        }
        if (confirmedStatus == null || confirmedStatus === '') {
            return { result: 'unconfirmed' };
        }
        return { result: 'mismatch', observedStatus: confirmedStatus };
    } catch (error) {
        console.warn('Could not re-fetch form status after save:', error);
        return { result: 'unconfirmed' };
    }
}

export type FormBuilderProps = {
    variant?: 'page' | 'overlay';
    formId?: string;
    templateId?: string;
    /** Prefill a new form from an existing company form (copy). */
    sourceFormId?: string;
    isNewForm?: boolean;
    isEditTemplateMode?: boolean;
    allowedFormTypes?: string[];
    lockFormType?: boolean;
    defaultFormType?: string;
    defaultFormName?: string;
    manageNavigationGuard?: boolean;
    onSaved?: (form: FormBuilderSavedForm, status: 'draft' | 'published') => void | Promise<void>;
    onCancel?: () => void;
    onDirtyChange?: (dirty: boolean) => void;
    pageTitle?: string;
};

export const FormBuilder = ({
    variant = 'page',
    formId,
    templateId,
    sourceFormId,
    isNewForm = false,
    isEditTemplateMode = false,
    allowedFormTypes,
    lockFormType = false,
    defaultFormType = FORM_TYPES.INSPECTION,
    defaultFormName = '',
    manageNavigationGuard = variant === 'page',
    onSaved,
    onCancel,
    onDirtyChange,
    pageTitle,
}: FormBuilderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { companyForms, refreshForms, formTemplates } = useFormsContext();
    const { company, companyOwner, companyUser } = useUserContext();
    const { companyEquipmentCategories } = useEquipmentContext();
    const { registerGuard, tryNavigate, confirmPendingNavigation, cancelPendingNavigation } = useNavigationGuard();

    const resolvedAllowedFormTypes = allowedFormTypes?.length
        ? allowedFormTypes
        : [FORM_TYPES.INSPECTION, FORM_TYPES.DAILY_SERVICE];
    const isOverlay = variant === 'overlay';

    const [formName, setFormName] = useState(defaultFormName);
    // New inspection/daily forms start with one empty section so users can add items immediately.
    const initialContents: tFormPrompts[] =
        isNewForm && !isEditTemplateMode ? [{ type: DEFAULT_FORM_INPUT_TYPES.section, name: '', prompts: [] }] : [];
    const [formContents, setFormContents] = useState<tFormPrompts[]>(initialContents);
    const formContentsRef = useRef(formContents);
    formContentsRef.current = formContents;
    const catalogSaveInFlightRef = useRef(new Map<string, Promise<void>>());
    const [noMachineUMCounter, setNoMachineUMCounter] = useState(false);
    const [alwaysTriggerEmail, setAlwaysTriggerEmail] = useState(false);
    const [formTypeSelection, setFormTypeSelection] = useState<string>(defaultFormType);
    const [equipmentType, setEquipmentType] = useState<string>('');
    const [umCounterLabel, setUmCounterLabel] = useState<string>('hours');
    const [showCategoryOverlay, setShowCategoryOverlay] = useState(false);
    const [pendingCategorySearchText, setPendingCategorySearchText] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [persistedFormId, setPersistedFormId] = useState<string | undefined>(formId);
    const [savedFormStatus, setSavedFormStatus] = useState<'draft' | 'published' | null>(null);
    const [isFormCopy, setIsFormCopy] = useState(
        () => Boolean(isNewForm && (location.state as { isFormCopy?: boolean } | null)?.isFormCopy),
    );

    const [showDeletePromptModal, setShowDeletePromptModal] = useState(false);
    const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
    const [deletePromptIndex, setDeletePromptIndex] = useState<number | null>(null);
    const [deleteSectionIndex, setDeleteSectionIndex] = useState<number | null>(null);
    const [deletePromptInSectionIndex, setDeletePromptInSectionIndex] = useState<{
        sectionIndex: number;
        promptIndex: number;
    } | null>(null);

    const [showExitModal, setShowExitModal] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
    const [expandedItemIndex, setExpandedItemIndex] = useState<{ sectionIndex: number; promptIndex: number } | null>(
        null,
    );
    const didAutoExpandEmptyItemRef = useRef(false);
    const pendingFocusItemRef = useRef<{ sectionIndex: number; promptIndex: number } | null>(null);
    const [showUnsupportedWarning, setShowUnsupportedWarning] = useState(false);
    const [unsupportedCount, setUnsupportedCount] = useState(0);

    const [serviceItems, setServiceItems] = useState<PMPartsAndProcedure[]>([]);
    const [serviceProcedures, setServiceProcedures] = useState<ServiceProcedure[]>([]);
    const [procedureLinks, setProcedureLinks] = useState<ServiceProcedureConsumable[]>([]);
    const [procedureModalTarget, setProcedureModalTarget] = useState<{
        sectionIndex: number;
        promptIndex: number;
        initialName: string;
    } | null>(null);
    const [extraServiceCategories, setExtraServiceCategories] = useState<string[]>([]);
    const [catalogChangeRestore, setCatalogChangeRestore] = useState<Record<string, DsSectionCatalogKind>>({});
    const [changingCatalogKeys, setChangingCatalogKeys] = useState<Set<string>>(new Set());
    const lastCatalogKindBySectionRef = useRef<Record<string, DsSectionCatalogKind>>({});
    const categoryCreateRef = useRef<Map<string, Promise<PMPartsAndProcedure | null>>>(new Map());
    const serviceItemsRef = useRef<PMPartsAndProcedure[]>([]);
    serviceItemsRef.current = serviceItems;

    // Vendor state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [vendors, setVendors] = useState<any[]>([]);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [vendorDraftName, setVendorDraftName] = useState('');
    const [vendorPromptTarget, setVendorPromptTarget] = useState<{ sectionIndex: number; promptIndex: number } | null>(
        null,
    );

    const [initialFormState, setInitialFormState] = useState<{
        name: string;
        contents: string;
        noUM: boolean;
        email: boolean;
        formType: string;
        equipmentType: string;
        umCounterLabel: string;
    }>({
        name: defaultFormName,
        contents: JSON.stringify(initialContents),
        noUM: false,
        email: false,
        formType: defaultFormType,
        equipmentType: '',
        umCounterLabel: 'hours',
    });

    const companyId = (company?.id as string) || companyOwner?.companyId || '';
    const draftUserId = (companyUser?.id as string) || (companyOwner?.id as string) || '';

    const draftStorageKey = useMemo(
        () =>
            draftUserId
                ? formBuilderDraftKey({
                      companyId,
                      userId: draftUserId,
                      formId: formId || persistedFormId,
                      templateId,
                      isNewForm,
                      isEditTemplateMode,
                      formType: formTypeSelection || defaultFormType,
                  })
                : '',
        [
            companyId,
            draftUserId,
            formId,
            persistedFormId,
            templateId,
            isNewForm,
            isEditTemplateMode,
            formTypeSelection,
            defaultFormType,
        ],
    );
    const restoredDraftRef = useRef(false);
    const hydratedFromServerRef = useRef(false);
    const draftStorageKeyRef = useRef(draftStorageKey);
    draftStorageKeyRef.current = draftStorageKey;

    useEffect(() => {
        setPersistedFormId(formId);
    }, [formId]);

    // Restore in-progress draft after remount (e.g. Mac lock/unlock auth refresh).
    useEffect(() => {
        if (restoredDraftRef.current) return;
        if (!draftUserId || !draftStorageKey) return;
        const draft = readFormBuilderDraft(draftStorageKey);
        if (!draft) return;
        // Overlay Add New must not reopen a form that was already saved (shared :new leftover).
        if (isOverlay && isNewForm && draft.persistedFormId) {
            clearFormBuilderNewFormDrafts(companyId, formTypeSelection || defaultFormType, draftUserId);
            return;
        }
        restoredDraftRef.current = true;
        hydratedFromServerRef.current = true;
        setFormName(draft.formName);
        setFormContents(draft.formContents);
        setNoMachineUMCounter(draft.noMachineUMCounter);
        setAlwaysTriggerEmail(draft.alwaysTriggerEmail);
        setFormTypeSelection(draft.formTypeSelection);
        setEquipmentType(draft.equipmentType);
        setUmCounterLabel(draft.umCounterLabel);
        if (draft.persistedFormId) setPersistedFormId(draft.persistedFormId);
        if (draft.isFormCopy) setIsFormCopy(true);
        setInitialFormState(draft.initialFormState);
    }, [
        draftStorageKey,
        draftUserId,
        companyId,
        isNewForm,
        isOverlay,
        formTypeSelection,
        defaultFormType,
    ]);

    // Keep published/draft status in sync for edit pages (incl. after draft remount).
    useEffect(() => {
        if (!formId || isNewForm || isEditTemplateMode) return;
        const existingForm = companyForms.find((f) => f.id === formId);
        if (!existingForm) return;
        setSavedFormStatus(existingForm.status === 'draft' ? 'draft' : 'published');
    }, [formId, isNewForm, isEditTemplateMode, companyForms]);

    // Persist dirty drafts so wake/remount does not lose work.
    useEffect(() => {
        if (!hasUnsavedChanges || !draftStorageKey) return;
        const timer = window.setTimeout(() => {
            writeFormBuilderDraft(draftStorageKeyRef.current, {
                formName,
                formContents,
                noMachineUMCounter,
                alwaysTriggerEmail,
                formTypeSelection,
                equipmentType,
                umCounterLabel,
                persistedFormId,
                isFormCopy: isFormCopy || undefined,
                initialFormState,
            });
        }, 400);
        return () => window.clearTimeout(timer);
    }, [
        hasUnsavedChanges,
        formName,
        formContents,
        noMachineUMCounter,
        alwaysTriggerEmail,
        formTypeSelection,
        equipmentType,
        umCounterLabel,
        persistedFormId,
        isFormCopy,
        initialFormState,
        draftStorageKey,
    ]);

    const isEditMode = Boolean((!isNewForm && !isEditTemplateMode && formId) || persistedFormId);

    const isDailyService = formTypeSelection === FORM_TYPES.DAILY_SERVICE;
    const formsListHref = formsListPath(formTypeSelection);
    const currentFormId = persistedFormId || formId || null;
    const duplicateFormName = findDuplicateFormName(
        formName,
        companyForms,
        formTypeSelection,
        currentFormId,
    );
    const sortedCategoryOptions = useMemo(
        () => [
            { value: ALL_EQUIPMENT_CATEGORY_VALUE, label: 'All Equipment' },
            ...sortedEquipmentCategoryOptions(companyEquipmentCategories),
        ],
        [companyEquipmentCategories],
    );
    const formNameOptions = useMemo(
        () => uniqueSortedFormNameOptions(companyForms, formTypeSelection),
        [companyForms, formTypeSelection],
    );

    const handleFormTypeChange = (nextType: string) => {
        setFormTypeSelection(nextType);
        if (!isNewForm || isOverlay || isEditTemplateMode) return;
        const params = new URLSearchParams(location.search);
        params.set('type', formTypeToQueryParam(nextType));
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    };
    /** New Daily Service forms, saved catalog sections, or a section added during this edit. */
    const usesCatalogSuggest = (section?: tFormPrompts | tSectionContent | null) =>
        sectionUsesCatalogSuggest(isDailyService, section, { isNewForm });
    /** New items use Resources even when added to an older, free-text section. */
    const itemUsesCatalogSuggest = (
        prompt?: tPrompt | null,
        section?: tFormPrompts | tSectionContent | null,
    ) =>
        isDailyService &&
        !isDailyServiceProcedurePrompt(prompt) &&
        (isNewForm ||
            Boolean(section?.clientId) ||
            Boolean(prompt?.clientId) ||
            Boolean(prompt?.serviceItemId) ||
            !prompt?.component?.trim());
    const catalogActor = useMemo(
        () => ({
            companyId: companyId || '',
            createdByUserId: companyUser?.id || companyOwner?.id || '',
            createdByUserName: companyUser?.name || companyOwner?.name || '',
        }),
        [companyId, companyOwner?.id, companyOwner?.name, companyUser?.id, companyUser?.name],
    );

    useEffect(() => {
        if (!isDailyService || didAutoExpandEmptyItemRef.current) return;
        for (let sectionIndex = 0; sectionIndex < formContents.length; sectionIndex += 1) {
            const section = formContents[sectionIndex];
            const prompts = section.prompts || [];
            const promptIndex = prompts.findIndex(
                (prompt) =>
                    (itemUsesCatalogSuggest(prompt, section) &&
                        !prompt.component?.trim() &&
                        !prompt.serviceItemId) ||
                    (isDailyServiceProcedurePrompt(prompt) &&
                        !prompt.component?.trim() &&
                        !prompt.serviceProcedureId),
            );
            if (promptIndex < 0) continue;
            setExpandedItemIndex({ sectionIndex, promptIndex });
            didAutoExpandEmptyItemRef.current = true;
            return;
        }
    }, [formContents, isDailyService, isNewForm]);

    useEffect(() => {
        const pending = pendingFocusItemRef.current;
        if (!pending || !isDailyService) return;
        if (
            expandedItemIndex?.sectionIndex !== pending.sectionIndex ||
            expandedItemIndex?.promptIndex !== pending.promptIndex
        ) {
            return;
        }

        let cancelled = false;
        let innerFrame = 0;
        const focusNewItem = () => {
            if (cancelled) return;
            pendingFocusItemRef.current = null;
            const card = document.querySelector<HTMLElement>(
                `[data-ds-item-card="${pending.sectionIndex}-${pending.promptIndex}"]`,
            );
            card?.scrollIntoView({ behavior: 'smooth', block: 'start' });

            const section = formContents[pending.sectionIndex];
            const prompt = section?.prompts?.[pending.promptIndex];
            const promptKey =
                prompt?.clientId || prompt?.serviceItemId || `${pending.sectionIndex}-${pending.promptIndex}`;
            const input =
                document.getElementById(`ds-item-${promptKey}`) ||
                document.getElementById(`ds-item-${promptKey}-expanded`) ||
                card?.querySelector<HTMLInputElement>('.item-card-collapsed input, .item-card-expanded .form-field input');
            input?.focus({ preventScroll: true });
        };

        const frame = window.requestAnimationFrame(() => {
            innerFrame = window.requestAnimationFrame(focusNewItem);
        });
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(innerFrame);
        };
    }, [expandedItemIndex, formContents, isDailyService]);

    /* ─── DnD sensors ─── */
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const promptSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    /* ─── Collapse helpers ─── */
    const collapseAll = useCallback(() => {
        const sectionIndices = formContents
            .map((c, i) => (c.type === DEFAULT_FORM_INPUT_TYPES.section ? i : -1))
            .filter((i) => i !== -1);
        setCollapsedSections(new Set(sectionIndices));
    }, [formContents]);

    const expandAll = useCallback(() => {
        setCollapsedSections(new Set());
    }, []);

    /* ─── Top-level drag end ─── */
    const handleTopLevelDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = formContents.findIndex((_, i) => `item-${i}` === active.id);
        const newIndex = formContents.findIndex((_, i) => `item-${i}` === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        setCollapsedSections((prev) => {
            const next = new Set<number>();
            prev.forEach((idx) => {
                if (idx === oldIndex) next.add(newIndex);
                else if (oldIndex < newIndex && idx > oldIndex && idx <= newIndex) next.add(idx - 1);
                else if (oldIndex > newIndex && idx >= newIndex && idx < oldIndex) next.add(idx + 1);
                else next.add(idx);
            });
            return next;
        });

        setFormContents((prev) => arrayMove(prev, oldIndex, newIndex));
    };

    /* ─── Prompt drag end within a section ─── */
    const handlePromptDragEnd = (sectionIndex: number) => (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const section = formContents[sectionIndex];
        if (!section.prompts) return;

        const oldIndex = section.prompts.findIndex((_, i) => `prompt-${sectionIndex}-${i}` === active.id);
        const newIndex = section.prompts.findIndex((_, i) => `prompt-${sectionIndex}-${i}` === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        setFormContents((prev) => {
            const newContents = [...prev];
            const currentSection = newContents[sectionIndex];
            newContents[sectionIndex] = {
                ...currentSection,
                prompts: arrayMove(currentSection.prompts!, oldIndex, newIndex),
            };
            return newContents;
        });
    };

    /* ─── Effects ─── */
    useEffect(() => {
        if (hydratedFromServerRef.current || restoredDraftRef.current) return;
        if (isEditMode && formId && companyForms.length > 0) {
            const existingForm = companyForms.find((f) => f.id === formId);
            if (existingForm) {
                hydratedFromServerRef.current = true;
                const loadedContents = JSON.parse(existingForm.formData) as tFormPrompts[];
                setFormName(existingForm.name);
                setFormContents(loadedContents);
                setNoMachineUMCounter((existingForm.noUMCounter as boolean) || false);
                setAlwaysTriggerEmail((existingForm.alwaysTriggerEmail as boolean) || false);
                setEquipmentType(existingForm.equipmentType || '');
                setUmCounterLabel(existingForm.umCounterLabel || 'hours');
                const resolvedFormType = existingForm.formType
                    ? existingForm.formType
                    : existingForm.dailyServiceLogForm
                      ? FORM_TYPES.DAILY_SERVICE
                      : FORM_TYPES.INSPECTION;
                setFormTypeSelection(resolvedFormType);
                setSavedFormStatus(existingForm.status === 'draft' ? 'draft' : 'published');
                setInitialFormState({
                    name: existingForm.name,
                    contents: serializeFormContents(loadedContents),
                    noUM: (existingForm.noUMCounter as boolean) || false,
                    email: (existingForm.alwaysTriggerEmail as boolean) || false,
                    formType: resolvedFormType,
                    equipmentType: existingForm.equipmentType || '',
                    umCounterLabel: existingForm.umCounterLabel || 'hours',
                });
            }
        }
        if (isEditTemplateMode && templateId) {
            const existingForm = formTemplates.find((f) => f.id === templateId);
            if (existingForm) {
                hydratedFromServerRef.current = true;
                if (!formName) setFormName(existingForm.templateName);
                if (formContents.length === 0) setFormContents(JSON.parse(existingForm.templateData));
                setFormTypeSelection(
                    existingForm.dailyServiceLogForm ? FORM_TYPES.DAILY_SERVICE : FORM_TYPES.INSPECTION,
                );
                setInitialFormState({
                    name: existingForm.templateName,
                    contents: existingForm.templateData,
                    noUM: false,
                    email: false,
                    formType: existingForm.dailyServiceLogForm ? FORM_TYPES.DAILY_SERVICE : FORM_TYPES.INSPECTION,
                    equipmentType: '',
                    umCounterLabel: 'hours',
                });
            } else {
                refreshForms();
            }
        }
        if (isNewForm && sourceFormId && !isEditTemplateMode) {
            const sourceForm = companyForms.find((f) => f.id === sourceFormId);
            if (sourceForm) {
                hydratedFromServerRef.current = true;
                const resolvedFormType = sourceForm.formType
                    ? sourceForm.formType
                    : sourceForm.dailyServiceLogForm
                      ? FORM_TYPES.DAILY_SERVICE
                      : FORM_TYPES.INSPECTION;
                const copyName = `Copy of ${sourceForm.name?.trim() || 'Form'}`;
                setFormName(copyName);
                setFormContents(JSON.parse(sourceForm.formData));
                setNoMachineUMCounter((sourceForm.noUMCounter as boolean) || false);
                setAlwaysTriggerEmail((sourceForm.alwaysTriggerEmail as boolean) || false);
                setEquipmentType(sourceForm.equipmentType || '');
                setUmCounterLabel(sourceForm.umCounterLabel || 'hours');
                setFormTypeSelection(resolvedFormType);
                setInitialFormState({
                    name: copyName,
                    contents: sourceForm.formData,
                    noUM: (sourceForm.noUMCounter as boolean) || false,
                    email: (sourceForm.alwaysTriggerEmail as boolean) || false,
                    formType: resolvedFormType,
                    equipmentType: sourceForm.equipmentType || '',
                    umCounterLabel: sourceForm.umCounterLabel || 'hours',
                });
            } else {
                refreshForms();
            }
        }
    }, [formId, templateId, sourceFormId, companyForms, formTemplates, isEditMode, isEditTemplateMode, isNewForm]);

    useEffect(() => {
        const currentState = {
            name: formName,
            contents: serializeFormContents(formContents),
            noUM: noMachineUMCounter,
            email: alwaysTriggerEmail,
            formType: formTypeSelection,
            equipmentType,
            umCounterLabel,
        };
        const hasChanged =
            currentState.name !== initialFormState.name ||
            currentState.contents !== initialFormState.contents ||
            currentState.noUM !== initialFormState.noUM ||
            currentState.email !== initialFormState.email ||
            currentState.formType !== initialFormState.formType ||
            currentState.equipmentType !== initialFormState.equipmentType ||
            currentState.umCounterLabel !== initialFormState.umCounterLabel;
        setHasUnsavedChanges(hasChanged);
    }, [
        formName,
        formContents,
        noMachineUMCounter,
        alwaysTriggerEmail,
        formTypeSelection,
        equipmentType,
        umCounterLabel,
        initialFormState,
    ]);

    useEffect(() => {
        onDirtyChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    useEffect(() => {
        if (!manageNavigationGuard) {
            registerGuard(null);
            return () => registerGuard(null);
        }
        registerGuard(
            hasUnsavedChanges
                ? {
                      when: true,
                      onBlocked: () => setShowExitModal(true),
                  }
                : null,
        );

        return () => registerGuard(null);
    }, [hasUnsavedChanges, registerGuard, manageNavigationGuard]);

    // Fetch vendors for the vendor combobox
    useEffect(() => {
        const fetchVendors = async () => {
            if (!companyId) return;
            try {
                const data = await listVendorsByCompanyId(companyId);
                setVendors(data);
            } catch (err) {
                console.error('Error fetching vendors:', err);
            }
        };
        fetchVendors();
    }, [companyId]);

    useEffect(() => {
        if (!companyId || !isDailyService) return;
        let isCancelled = false;
        (async () => {
            try {
                const [itemsData, proceduresResult, linksResult] = await Promise.allSettled([
                    listServiceItemsByCompanyId(companyId),
                    listServiceProceduresByCompanyId(companyId),
                    listServiceProcedureConsumablesByCompanyId(companyId),
                ]);
                if (isCancelled) return;
                if (itemsData.status === 'fulfilled') {
                    setServiceItems((itemsData.value ?? []).filter(Boolean) as PMPartsAndProcedure[]);
                } else {
                    console.error('Error fetching service items:', itemsData.reason);
                }
                if (proceduresResult.status === 'fulfilled') {
                    setServiceProcedures(proceduresResult.value);
                } else {
                    console.error('Error fetching service procedures:', proceduresResult.reason);
                    setServiceProcedures([]);
                }
                if (linksResult.status === 'fulfilled') {
                    setProcedureLinks(linksResult.value);
                } else {
                    console.error('Error fetching procedure consumables:', linksResult.reason);
                    setProcedureLinks([]);
                }
            } catch (err) {
                console.error('Error fetching Daily Service catalogs:', err);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId, isDailyService]);

    const handleBackClick = () => {
        if (isOverlay || onCancel) {
            if (hasUnsavedChanges) {
                setShowExitModal(true);
                return;
            }
            onCancel?.();
            return;
        }
        tryNavigate(formsListHref);
    };

    const handleConfirmExit = () => {
        clearFormBuilderDraft(draftStorageKeyRef.current);
        clearFormBuilderNewFormDrafts(companyId, formTypeSelection || defaultFormType, draftUserId);
        setShowExitModal(false);
        setHasUnsavedChanges(false);
        if (isOverlay && onCancel) {
            onCancel();
            return;
        }
        confirmPendingNavigation();
    };

    const handleCancelExit = () => {
        setShowExitModal(false);
        cancelPendingNavigation();
    };

    const handleSubmit = async (e?: React.FormEvent, saveStatus: 'draft' | 'published' = 'published') => {
        if (e) e.preventDefault();
        if (!companyId) {
            setSaveError('Company context is not available. Please refresh and try again.');
            return;
        }

        const validationMessage = getFormValidationMessage(saveStatus);
        if (validationMessage) {
            setSaveError(validationMessage);
            return;
        }

        // Inspection-only gate: Daily Service forms intentionally use operator response
        // patterns (addedNotAdded, quantityOnly, etc.) that are not inspection subtypes.
        if (!isDailyService) {
            const SUPPORTED_INSPECT_TYPES = new Set([
                DEFAULT_FORM_INPUT_TYPES.fourResponse,
                DEFAULT_FORM_INPUT_TYPES.yesNo,
                DEFAULT_FORM_INPUT_TYPES.checkbox,
                DEFAULT_FORM_INPUT_TYPES.dropdown,
                DEFAULT_FORM_INPUT_TYPES.sectionQuestion,
                DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
                OPERATOR_RESPONSE_TYPES.YES_NO,
            ]);

            let unsupported = 0;
            for (const content of formContents) {
                if (content.prompts) {
                    for (const prompt of content.prompts) {
                        const type = prompt.operatorResponseType || prompt.type || '';
                        if (type && !SUPPORTED_INSPECT_TYPES.has(type)) {
                            unsupported++;
                        }
                    }
                }
            }

            if (unsupported > 0 && saveStatus === 'published' && !showUnsupportedWarning) {
                setUnsupportedCount(unsupported);
                setShowUnsupportedWarning(true);
                return;
            }
        }

        setShowUnsupportedWarning(false);
        setSaveError(null);
        setIsCreating(true);

        let contentsToSave = formContents;
        if (isDailyService) {
            try {
                contentsToSave = await persistCatalogFields(formContents);
            } catch (err) {
                console.error('Failed to sync catalog fields to Resources:', err);
            }
            contentsToSave = contentsToSave.map((section) => withResolvedCatalogKind(section));
        }

        // Use GraphQL mutations (not models.*) so status/equipmentType/umCounterLabel are
        // sent even when local amplify_outputs model_introspection is stale.
        const baseInput: CreateFormInput = {
            name: formName,
            formData: serializeFormContents(contentsToSave),
            noUMCounter: noMachineUMCounter,
            alwaysTriggerEmail,
            companyId,
            dailyServiceLogForm: formTypeSelection === FORM_TYPES.DAILY_SERVICE,
            inspectionForm:
                formTypeSelection === FORM_TYPES.INSPECTION || formTypeSelection === FORM_TYPES.SHIFT_REPORT,
            pmServiceScheduleForm: false,
            formType: formTypeSelection,
            status: saveStatus,
            equipmentType: equipmentType || null,
            umCounterLabel: umCounterLabel || null,
        };

        try {
            let savedForm: FormBuilderSavedForm | null = null;
            const editingId = persistedFormId || (isEditMode ? formId : undefined);
            if (editingId) {
                const updateInput: UpdateFormInput = { ...baseInput, id: editingId };
                const response = (await authClient.graphql({
                    query: updateFormMutation,
                    variables: { input: updateInput },
                })) as {
                    data?: { updateForm?: { id?: string; name?: string; status?: string | null } };
                    errors?: Array<{ message: string }>;
                };
                if (response.errors?.length) {
                    throw new Error(response.errors.map((err) => err.message).join('; '));
                }
                const updated = response.data?.updateForm;
                if (!updated?.id) {
                    throw new Error('Form was updated but no id was returned.');
                }
                const updateStatusCheck = await confirmSavedFormStatus(
                    updated.id,
                    saveStatus,
                    updated.status,
                );
                if (updateStatusCheck.result === 'mismatch') {
                    throw new Error(
                        `Form status was not saved as ${saveStatus} (got ${updateStatusCheck.observedStatus}). Please try again.`,
                    );
                }
                if (updateStatusCheck.result === 'unconfirmed') {
                    console.warn(
                        `Form ${updated.id} saved but status could not be confirmed as ${saveStatus} (mutation returned ${updated.status ?? 'empty'}). Continuing without treating as failure.`,
                    );
                }
                savedForm = {
                    id: updated.id,
                    name: updated.name || formName,
                };
            } else {
                const response = (await authClient.graphql({
                    query: createFormMutation,
                    variables: { input: baseInput },
                })) as {
                    data?: { createForm?: { id?: string; name?: string; status?: string | null } };
                    errors?: Array<{ message: string }>;
                };
                if (response.errors?.length) {
                    throw new Error(response.errors.map((err) => err.message).join('; '));
                }
                const created = response.data?.createForm;
                if (!created?.id) {
                    throw new Error('Form was created but no id was returned.');
                }
                const createStatusCheck = await confirmSavedFormStatus(
                    created.id,
                    saveStatus,
                    created.status,
                );
                if (createStatusCheck.result === 'mismatch') {
                    throw new Error(
                        `Form status was not saved as ${saveStatus} (got ${createStatusCheck.observedStatus}). Please try again.`,
                    );
                }
                if (createStatusCheck.result === 'unconfirmed') {
                    console.warn(
                        `Form ${created.id} saved but status could not be confirmed as ${saveStatus} (mutation returned ${created.status ?? 'empty'}). Continuing without treating as failure.`,
                    );
                }
                savedForm = { id: created.id, name: created.name || formName };
                setPersistedFormId(created.id);
            }
            await refreshForms();
            setHasUnsavedChanges(false);
            setSavedFormStatus(saveStatus);
            clearFormBuilderDraft(draftStorageKeyRef.current);
            clearFormBuilderNewFormDrafts(companyId, formTypeSelection || defaultFormType, draftUserId);
            // Catalog sync stamps fields (including catalogKind) only on the saved copy.
            // Keep editor state on that same copy so Back does not see a false diff.
            setFormContents(contentsToSave);
            setInitialFormState({
                name: formName,
                contents: serializeFormContents(contentsToSave),
                noUM: noMachineUMCounter,
                email: alwaysTriggerEmail,
                formType: formTypeSelection,
                equipmentType,
                umCounterLabel,
            });
            if (onSaved && savedForm) {
                await onSaved(savedForm, saveStatus);
            } else if (savedForm && isNewForm && !isEditTemplateMode) {
                // Stay in the builder after the first save; only Back/Cancel leaves the page.
                navigate(
                    `/forms/edit/${savedForm.id}?type=${formTypeToQueryParam(formTypeSelection)}`,
                    { replace: true },
                );
            }
        } catch (error) {
            console.error('Error saving form:', error);
            setSaveError(
                error instanceof Error
                    ? error.message
                    : saveStatus === 'draft'
                      ? 'Failed to save draft. Please try again.'
                      : 'Failed to publish form. Please try again.',
            );
        } finally {
            setIsCreating(false);
        }
    };

    const handleCreateFormCopy = () => {
        if (!formId || isNewForm || isEditTemplateMode || isCreating || !draftUserId) return;

        const clonedName = `Copy of ${formName.trim() || 'Form'}`;
        const contentsCopy = JSON.parse(serializeFormContents(formContents)) as tFormPrompts[];
        writeFormBuilderDraft(
            formBuilderDraftKey({
                companyId,
                userId: draftUserId,
                isNewForm: true,
                formType: formTypeSelection,
            }),
            {
            formName: clonedName,
            formContents: contentsCopy,
            noMachineUMCounter,
            alwaysTriggerEmail,
            formTypeSelection,
            equipmentType,
            umCounterLabel,
            isFormCopy: true,
            initialFormState: {
                name: '',
                contents: '[]',
                noUM: false,
                email: false,
                formType: formTypeSelection,
                equipmentType: '',
                umCounterLabel: 'hours',
            },
        });

        // Leave the original form untouched; open an unsaved new form with the copy.
        setHasUnsavedChanges(false);
        clearFormBuilderDraft(draftStorageKeyRef.current);
        registerGuard(null);
        navigate('/forms/new', { state: { isFormCopy: true } });
    };

    const addQuestion = () => {
        setFormContents([
            ...formContents,
            {
                type: DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
                prompt: '',
                inputMode: { label: 'Text', value: 'text' },
            },
        ]);
    };

    const createDailyServicePrompt = (fromCatalog: boolean): tPrompt => ({
        ...(fromCatalog ? { clientId: crypto.randomUUID() } : {}),
        component: '',
        description: '',
        category: FORM_ITEM_CATEGORIES.NORMAL_CONSUMABLE,
        ...promptPatchForDailyServiceCategory(FORM_ITEM_CATEGORIES.NORMAL_CONSUMABLE),
    });

    const addSection = (catalogKind?: (typeof DS_SECTION_CATALOG)[keyof typeof DS_SECTION_CATALOG]) => {
        setFormContents([
            ...formContents,
            {
                type: DEFAULT_FORM_INPUT_TYPES.section,
                name: '',
                prompts: [],
                ...(isDailyService ? { clientId: crypto.randomUUID() } : {}),
                ...(isDailyService && catalogKind ? { catalogKind } : {}),
            },
        ]);
    };

    const promptHasLabel = (prompt: tPrompt) =>
        Boolean(
            prompt.component?.trim() || prompt.description?.trim() || (prompt as { prompt?: string }).prompt?.trim(),
        );

    const contentIsReadyForDraft = (content: tFormPrompts) => {
        if (content.type === DEFAULT_FORM_INPUT_TYPES.section) {
            // Section draft: named, or has at least one labeled item (inspection uses `component`)
            return Boolean(content.name?.trim()) || (content.prompts || []).some(promptHasLabel);
        }
        return Boolean(content.prompt?.trim());
    };

    const contentIsReadyForPublish = (content: tFormPrompts) => {
        if (content.type === DEFAULT_FORM_INPUT_TYPES.section) {
            return Boolean(content.name?.trim()) && (content.prompts || []).some(promptHasLabel);
        }
        return Boolean(content.prompt?.trim());
    };

    const getFormValidationMessage = (saveStatus: 'draft' | 'published' = 'published') => {
        if (!formName?.trim()) return 'Enter a form name to continue.';
        if (duplicateFormName) return duplicateFormNameMessage(formName);
        if (formContents.length === 0) return 'Add at least one section or question.';

        if (saveStatus === 'draft') {
            if (!formContents.every(contentIsReadyForDraft)) {
                return 'Name each section or add at least one labeled item / question.';
            }
            return null;
        }

        if (!formContents.every(contentIsReadyForPublish)) {
            return 'Name each section and add at least one inspection item (or question) before publishing.';
        }
        return null;
    };

    const isFormValid = (saveStatus: 'draft' | 'published' = 'published') => !getFormValidationMessage(saveStatus);

    const formValidationHint = getFormValidationMessage('published');
    const isAlreadyPublished = savedFormStatus === 'published';
    const showPublishAction = !isAlreadyPublished || hasUnsavedChanges;
    const publishButtonLabel = isAlreadyPublished ? 'Publish Form Edits' : 'Publish Form';
    const canSaveDraft = isFormValid('draft') && !isCreating;
    const canPublish = isFormValid('published') && !isCreating && showPublishAction;
    const showCreateFormCopyAction = Boolean(formId && !isNewForm && !isEditTemplateMode);

    /* ─── Helper: update a prompt field in a section ─── */
    const updatePromptField = (sectionIndex: number, promptIndex: number, field: string, value: any) => {
        setFormContents((prev) => {
            const nc = [...prev];
            const sec = nc[sectionIndex];
            if (sec.prompts) {
                const up = [...sec.prompts];
                up[promptIndex] = { ...up[promptIndex], [field]: value };
                nc[sectionIndex] = { ...sec, prompts: up } as tFormPrompts;
            }
            return nc;
        });
    };

    const updatePromptFields = (sectionIndex: number, promptIndex: number, patch: Partial<tPrompt>) => {
        setFormContents((prev) => {
            const nc = [...prev];
            const sec = nc[sectionIndex];
            if (!sec.prompts) return prev;
            const up = [...sec.prompts];
            up[promptIndex] = { ...up[promptIndex], ...patch };
            nc[sectionIndex] = { ...sec, prompts: up } as tFormPrompts;
            return nc;
        });
    };

    const bindProcedureToPrompt = (sectionIndex: number, promptIndex: number, procedure: ServiceProcedure) => {
        updatePromptFields(
            sectionIndex,
            promptIndex,
            applyProcedureToPrompt(procedure, procedureLinks, serviceItems),
        );
    };

    const addPromptToSection = (sectionIndex: number, newPrompt: tPrompt) => {
        const nextPromptIndex = (formContents[sectionIndex]?.prompts || []).length;
        setFormContents((prev) => {
            const nc = [...prev];
            const sec = nc[sectionIndex];
            nc[sectionIndex] = {
                ...sec,
                prompts: [...(sec.prompts || []), newPrompt],
            } as tFormPrompts;
            return nc;
        });
        pendingFocusItemRef.current = { sectionIndex, promptIndex: nextPromptIndex };
        setExpandedItemIndex({ sectionIndex, promptIndex: nextPromptIndex });
        didAutoExpandEmptyItemRef.current = true;
    };

    const setSectionCategoryName = (sectionIndex: number, name: string) => {
        setFormContents((prev) => {
            const section = prev[sectionIndex];
            if (!section) return prev;
            return [
                ...prev.slice(0, sectionIndex),
                { ...section, name },
                ...prev.slice(sectionIndex + 1),
            ];
        });
    };

    const sectionCatalogChangeKey = (section: tSectionContent, sectionIndex: number) =>
        section.clientId || `section-${sectionIndex}`;

    const setSectionCatalogKind = (
        sectionIndex: number,
        catalogKind: (typeof DS_SECTION_CATALOG)[keyof typeof DS_SECTION_CATALOG] | null,
    ) => {
        setFormContents((prev) => {
            const section = prev[sectionIndex];
            if (!section) return prev;
            if (catalogKind) {
                return [
                    ...prev.slice(0, sectionIndex),
                    { ...section, catalogKind },
                    ...prev.slice(sectionIndex + 1),
                ];
            }
            const nextSection = { ...section };
            delete nextSection.catalogKind;
            return [
                ...prev.slice(0, sectionIndex),
                nextSection,
                ...prev.slice(sectionIndex + 1),
            ];
        });
    };

    const beginCatalogChange = (sectionIndex: number, section: tSectionContent) => {
        const key = sectionCatalogChangeKey(section, sectionIndex);
        const previousKind = resolveSectionCatalogKind(section);
        if (previousKind) {
            lastCatalogKindBySectionRef.current[key] = previousKind;
            setCatalogChangeRestore((prev) => ({ ...prev, [key]: previousKind }));
        }
        setChangingCatalogKeys((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };

    const cancelCatalogChange = (sectionIndex: number, section: tSectionContent) => {
        const key = sectionCatalogChangeKey(section, sectionIndex);
        const previousKind =
            catalogChangeRestore[key] ||
            lastCatalogKindBySectionRef.current[key] ||
            resolveSectionCatalogKind(section) ||
            DS_SECTION_CATALOG.SERVICE_ITEMS;
        setSectionCatalogKind(sectionIndex, previousKind);
        setChangingCatalogKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        setCatalogChangeRestore((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const commitCatalogKind = (
        sectionIndex: number,
        section: tSectionContent,
        catalogKind: DsSectionCatalogKind,
    ) => {
        const key = sectionCatalogChangeKey(section, sectionIndex);
        lastCatalogKindBySectionRef.current[key] = catalogKind;
        setSectionCatalogKind(sectionIndex, catalogKind);
        setChangingCatalogKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        setCatalogChangeRestore((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const rememberExtraCategory = (name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        setExtraServiceCategories((prev) =>
            prev.some((category) => category.toLowerCase() === trimmed.toLowerCase()) ? prev : [...prev, trimmed],
        );
    };

    const upsertCatalogItem = (item: PMPartsAndProcedure) => {
        setServiceItems((prev) => {
            const index = prev.findIndex((existing) => existing.id === item.id);
            const next =
                index === -1
                    ? [...prev, item]
                    : prev.map((existing, i) => (i === index ? { ...existing, ...item } : existing));
            serviceItemsRef.current = next;
            return next;
        });
    };

    const ensureCategoryInResources = async (name: string) => {
        const trimmed = name.trim();
        if (!trimmed || !catalogActor.companyId) return trimmed;
        const existing = findCategoryCasing(serviceItemsRef.current, trimmed);
        if (existing) return existing;

        const key = trimmed.toLowerCase();
        const inFlight = categoryCreateRef.current.get(key);
        if (inFlight) {
            const created = await inFlight;
            return created?.category?.trim() || trimmed;
        }

        const createPromise = createCategoryStub(trimmed, catalogActor)
            .then((created) => {
                if (created) upsertCatalogItem(created);
                return created;
            })
            .catch((err) => {
                console.error('Failed to add category to Resources:', err);
                return null;
            })
            .finally(() => {
                categoryCreateRef.current.delete(key);
            });

        categoryCreateRef.current.set(key, createPromise);
        const created = await createPromise;
        return created?.category?.trim() || trimmed;
    };

    const handleSectionCategoryChange = (sectionIndex: number, value: string) => {
        const trimmed = value.trim();
        const kind = resolveSectionCatalogKind(formContents[sectionIndex]);
        const canonical =
            kind === DS_SECTION_CATALOG.PROCEDURES
                ? findProcedureCategoryCasing(serviceProcedures, trimmed) || trimmed
                : findCategoryCasing(serviceItems, trimmed) || trimmed;
        setSectionCategoryName(sectionIndex, canonical);
    };

    const handleAddSectionCategory = async (sectionIndex: number, searchText: string) => {
        const trimmed = searchText.trim();
        if (!trimmed) return;
        const kind = resolveSectionCatalogKind(formContents[sectionIndex]);
        const canonical =
            kind === DS_SECTION_CATALOG.PROCEDURES
                ? findProcedureCategoryCasing(serviceProcedures, trimmed) || trimmed
                : findCategoryCasing(serviceItems, trimmed) || trimmed;
        rememberExtraCategory(canonical);
        setSectionCategoryName(sectionIndex, canonical);
        if (shouldStubServiceItemCategory(canonical, serviceItemsRef.current, serviceProcedures, kind)) {
            await ensureCategoryInResources(canonical);
        }
    };

    const applyCatalogItemToPrompt = (
        sectionIndex: number,
        promptIndex: number,
        item: PMPartsAndProcedure,
    ) => {
        setFormContents((prev) => {
            const section = prev[sectionIndex];
            if (!section?.prompts?.[promptIndex]) return prev;
            const patch = catalogFieldsForPrompt(item, vendors, section.prompts[promptIndex]);
            const prompts = [...section.prompts];
            prompts[promptIndex] = { ...prompts[promptIndex], ...patch };
            const next = [...prev];
            next[sectionIndex] = { ...section, prompts } as tFormPrompts;
            return next;
        });
    };

    const handleAddCatalogItem = (
        sectionIndex: number,
        promptIndex: number,
        categoryName: string,
        searchText: string,
    ) => {
        const flightKey = `${sectionIndex}:${promptIndex}`;
        const flight = (async () => {
            const trimmed = searchText.trim();
            const category = categoryName.trim();
            if (!trimmed || !category || !catalogActor.companyId) return;
            try {
                await ensureCategoryInResources(category);
                const prompt = formContentsRef.current[sectionIndex]?.prompts?.[promptIndex];
                let item = await createOrFillCatalogItem(
                    category,
                    trimmed,
                    serviceItemsRef.current,
                    catalogActor,
                    catalogFieldsFromPrompt(prompt || {}, vendors),
                );
                if (!item) return;
                upsertCatalogItem(item);
                const latest = formContentsRef.current[sectionIndex]?.prompts?.[promptIndex];
                const pending = latest ? pendingListingOverrides(latest, item) : null;
                if (pending && latest) {
                    const synced = await persistPromptListingToCatalog(
                        {
                            ...latest,
                            component: latest.component?.trim() || trimmed,
                            serviceItemId: item.id,
                        },
                        category,
                        vendors,
                        pending,
                    );
                    if (synced) {
                        item = synced;
                        upsertCatalogItem(item);
                    }
                }
                applyCatalogItemToPrompt(sectionIndex, promptIndex, item);
            } catch (err) {
                console.error('Failed to add item to Resources:', err);
                updatePromptFields(sectionIndex, promptIndex, { component: trimmed, serviceItemId: undefined });
            }
        })();
        catalogSaveInFlightRef.current.set(flightKey, flight);
        void flight.finally(() => {
            if (catalogSaveInFlightRef.current.get(flightKey) === flight) {
                catalogSaveInFlightRef.current.delete(flightKey);
            }
        });
    };

    const persistPromptListingToCatalog = async (
        prompt: tPrompt,
        categoryName: string,
        vendorList = vendors,
        overrides?: { partNumber?: string; vendorName?: string; manufacturer?: string },
    ): Promise<PMPartsAndProcedure | null> => {
        const name = prompt.component?.trim();
        const category = categoryName.trim();
        if (!name || !category || !catalogActor.companyId) return null;
        const source = prompt.serviceItemId
            ? serviceItemsRef.current.find((item) => item.id === prompt.serviceItemId) ?? null
            : null;
        try {
            const item = await resolveOrCreateCatalogListing(
                category,
                name,
                serviceItemsRef.current,
                catalogActor,
                catalogFieldsFromPrompt(prompt, vendorList, overrides),
                source,
            );
            if (item) upsertCatalogItem(item);
            return item;
        } catch (err) {
            console.error('Failed to save listing to Resources:', err);
            return null;
        }
    };

    const persistCatalogFields = async (contents: tFormPrompts[]): Promise<tFormPrompts[]> => {
        let next = contents;
        for (let sectionIndex = 0; sectionIndex < next.length; sectionIndex += 1) {
            const section = next[sectionIndex];
            const prompts = section.prompts || [];
            for (let promptIndex = 0; promptIndex < prompts.length; promptIndex += 1) {
                const prompt = prompts[promptIndex];
                if (!itemUsesCatalogSuggest(prompt, section) || !prompt.component?.trim()) continue;
                const item = await persistPromptListingToCatalog(prompt, section.name || '');
                if (!item) continue;
                const up = [...(next[sectionIndex].prompts || [])];
                up[promptIndex] = { ...up[promptIndex], ...catalogFieldsForPrompt(item, vendors, prompt) };
                next = [
                    ...next.slice(0, sectionIndex),
                    { ...next[sectionIndex], prompts: up },
                    ...next.slice(sectionIndex + 1),
                ];
            }
        }
        setFormContents(next);
        return next;
    };

    /* ─── Prompt action buttons ─── */
    const renderPromptActions = (sectionIndex: number, promptIndex: number) => (
        <div className="table-actions prompt-actions">
            <BtnIconOnly
                size="sm"
                icon={<IconCopy />}
                ariaLabel="Duplicate item"
                onClick={() => {
                    setFormContents((prev) => {
                        const newContents = [...prev];
                        const section = newContents[sectionIndex];
                        if (section.prompts) {
                            const duplicate = { ...section.prompts[promptIndex] };
                            newContents[sectionIndex] = {
                                ...section,
                                prompts: [
                                    ...section.prompts.slice(0, promptIndex + 1),
                                    duplicate,
                                    ...section.prompts.slice(promptIndex + 1),
                                ],
                            };
                        }
                        return newContents;
                    });
                }}
            />
            <BtnIconOnly
                size="sm"
                variant="danger"
                icon={<IconTrash />}
                ariaLabel="Delete item"
                onClick={() => {
                    setShowDeletePromptModal(true);
                    setDeletePromptInSectionIndex({ sectionIndex, promptIndex });
                }}
            />
        </div>
    );

    const renderItemNameSuggest = (
        prompt: tPrompt,
        sectionIndex: number,
        promptIndex: number,
        sectionCategory: string,
        className: string,
    ) => {
        const categoryItems = sortServiceItemsForListing(
            itemsInCategory(serviceItems, sectionCategory),
            vendors,
        );
        const itemOptions = categoryItems.map((item) => ({
            value: item.id,
            label: serviceItemOptionLabel(item, vendors, categoryItems),
        }));
        const selectedValue = prompt.serviceItemId || prompt.component || '';
        const promptKey = prompt.clientId || prompt.serviceItemId || `${sectionIndex}-${promptIndex}`;

        const commitTypedName = (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || !sectionCategory.trim()) return;
            if (
                prompt.serviceItemId &&
                prompt.component?.trim().toLowerCase() === trimmed.toLowerCase()
            ) {
                return;
            }

            const byId = categoryItems.find((item) => item.id === trimmed);
            if (byId) {
                applyCatalogItemToPrompt(sectionIndex, promptIndex, byId);
                return;
            }

            const byLabel = itemOptions.filter(
                (option) => option.label.toLowerCase() === trimmed.toLowerCase(),
            );
            if (byLabel.length === 1) {
                const item = categoryItems.find((entry) => entry.id === byLabel[0].value);
                if (item) applyCatalogItemToPrompt(sectionIndex, promptIndex, item);
                return;
            }

            const sameName = categoryItems.filter(
                (item) => item.itemName.trim().toLowerCase() === trimmed.toLowerCase(),
            );
            if (sameName.length === 1) {
                applyCatalogItemToPrompt(sectionIndex, promptIndex, sameName[0]);
                return;
            }

            const listingMatch = findMatchingListing(serviceItems, sectionCategory, {
                itemName: trimmed,
                partNumber: prompt.partNumber,
                manufacturer: prompt.partDescription,
                ...resolveVendorFromName(vendors, prompt.vendor),
            });
            if (listingMatch) {
                applyCatalogItemToPrompt(sectionIndex, promptIndex, listingMatch);
                return;
            }

            void handleAddCatalogItem(sectionIndex, promptIndex, sectionCategory, trimmed);
        };

        return (
            <div
                className="item-card-name-suggest-wrap"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onBlur={(event) => {
                    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                    const typed = (event.currentTarget.querySelector('input') as HTMLInputElement | null)?.value;
                    if (typed?.trim()) commitTypedName(typed);
                }}
            >
                <SearchSuggest
                    key={promptKey}
                    className={className}
                    options={itemOptions}
                    value={selectedValue}
                    onChange={(itemId) => {
                        const item = serviceItems.find((entry) => entry.id === itemId);
                        if (item) applyCatalogItemToPrompt(sectionIndex, promptIndex, item);
                    }}
                    placeholder="Search or add item"
                    disabled={!sectionCategory.trim()}
                    disabledMessage="Select a category first"
                    emptyMessage="No items in this category"
                    actionOption={{
                        label: '+ Add new item',
                        typedOnly: true,
                        onClick: ({ searchText }) => commitTypedName(searchText),
                    }}
                    formatDisplayValue={(itemId) => {
                        const item = serviceItems.find((entry) => entry.id === itemId);
                        return item?.itemName?.trim() || prompt.component || '';
                    }}
                    inputId={`ds-item-${promptKey}`}
                    ariaLabel="Item name"
                />
            </div>
        );
    };

    const renderProcedureNameSuggest = (
        prompt: tPrompt,
        sectionIndex: number,
        promptIndex: number,
        className: string,
    ) => {
        const procedureOptions = sortedProcedureSuggestOptions(
            serviceProcedures,
            formContents[sectionIndex]?.name,
        );
        const selectedValue = prompt.serviceProcedureId || prompt.component || '';
        const promptKey = prompt.clientId || prompt.serviceProcedureId || `${sectionIndex}-${promptIndex}`;

        const commitTypedProcedure = (text: string, allowEmpty = false) => {
            const trimmed = text.trim();
            if (!trimmed && !allowEmpty) return;
            const bound = prompt.serviceProcedureId
                ? serviceProcedures.find((entry) => entry.id === prompt.serviceProcedureId)
                : undefined;
            if (trimmed && isBoundProcedureSuggestText(trimmed, bound, prompt.component)) {
                return;
            }
            if (trimmed) {
                const match = findProcedureForSuggestInput(serviceProcedures, trimmed);
                if (match) {
                    bindProcedureToPrompt(sectionIndex, promptIndex, match);
                    return;
                }
            }
            setProcedureModalTarget({ sectionIndex, promptIndex, initialName: trimmed });
        };

        return (
            <div
                className="item-card-name-suggest-wrap"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onBlur={(event) => {
                    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                    const typed = (event.currentTarget.querySelector('input') as HTMLInputElement | null)?.value;
                    if (typed?.trim()) commitTypedProcedure(typed);
                }}
            >
                <SearchSuggest
                    key={promptKey}
                    className={className}
                    options={procedureOptions}
                    value={selectedValue}
                    onChange={(procedureId) => {
                        const selected = serviceProcedures.find((entry) => entry.id === procedureId);
                        if (selected) bindProcedureToPrompt(sectionIndex, promptIndex, selected);
                    }}
                    placeholder="Search procedures (this heading first)"
                    emptyMessage="No service procedures yet"
                    actionOption={{
                        label: '+ Add new procedure',
                        onClick: ({ searchText }) => commitTypedProcedure(searchText, true),
                    }}
                    formatDisplayValue={(procedureId) => {
                        const selected = serviceProcedures.find((entry) => entry.id === procedureId);
                        return selected?.itemName?.trim() || prompt.component || '';
                    }}
                    inputId={`ds-procedure-${promptKey}`}
                    ariaLabel="Procedure"
                />
            </div>
        );
    };

    /* ─── Render collapsed item card (daily service) ─── */
    const getCollapsedItemMeta = (prompt: tPrompt) => {
        const partMeta = prompt.partDescription?.trim() || prompt.vendor?.trim() || '';
        const um = prompt.defaultUM?.trim() || '';
        if (partMeta && um) return `${partMeta} · ${um}`;
        return partMeta || um || '';
    };

    const renderCollapsedItemCard = (
        prompt: tPrompt,
        sectionIndex: number,
        promptIndex: number,
        dragHandleProps?: Record<string, unknown>,
    ) => {
        const categoryOption = isDailyServiceProcedurePrompt(prompt)
            ? { value: FORM_ITEM_CATEGORIES.PROCEDURE, shortLabel: 'Procedure', label: 'Procedure' }
            : FORM_ITEM_CATEGORY_OPTIONS.find((c) => c.value === prompt.category);
        const responseOption = isDailyServiceProcedurePrompt(prompt)
            ? { value: OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT, label: 'Acknowledgement' }
            : OPERATOR_RESPONSE_TYPE_OPTIONS.find((r) => r.value === prompt.operatorResponseType);
        const isExpanded =
            expandedItemIndex?.sectionIndex === sectionIndex && expandedItemIndex?.promptIndex === promptIndex;
        const metaLine = getCollapsedItemMeta(prompt);

        return (
            <div
                className={`item-card-collapsed ${isExpanded ? 'expanded' : ''}`}
                onClick={() =>
                    setExpandedItemIndex(isExpanded ? null : { sectionIndex, promptIndex })
                }
            >
                {dragHandleProps && (
                    <button
                        type="button"
                        className="item-card-drag-handle"
                        aria-label="Drag to reorder item"
                        title="Drag to reorder"
                        {...dragHandleProps}
                        onClick={(event) => event.stopPropagation()}
                        onPointerDown={(event) => {
                            event.stopPropagation();
                            const onPointerDown = dragHandleProps.onPointerDown;
                            if (typeof onPointerDown === 'function') {
                                (onPointerDown as (event: React.PointerEvent<HTMLButtonElement>) => void)(event);
                            }
                        }}
                    >
                        <IconDragHandle />
                    </button>
                )}
                <span className="item-card-status-dot" aria-hidden="true" />
                <div className="item-card-collapsed-content">
                    {isDailyServiceProcedurePrompt(prompt)
                        ? renderProcedureNameSuggest(prompt, sectionIndex, promptIndex, 'item-card-name-suggest')
                        : itemUsesCatalogSuggest(prompt, formContents[sectionIndex])
                          ? renderItemNameSuggest(
                                prompt,
                                sectionIndex,
                                promptIndex,
                                (formContents[sectionIndex] as tSectionContent | undefined)?.name || '',
                                'item-card-name-suggest',
                            )
                          : (
                            <span className="item-card-name">{prompt.component || 'Untitled Item'}</span>
                          )}
                    {categoryOption && (
                        <Badge
                            badgeClasses={getCategorySoftBadgeClass(categoryOption.value)}
                            badgeText={categoryOption.shortLabel}
                        />
                    )}
                    {responseOption && (
                        <Badge badgeClasses="badge-neutral" badgeText={responseOption.label} />
                    )}
                    {metaLine && <span className="item-card-tag meta-tag">{metaLine}</span>}
                    {prompt.autoFlag && (
                        <Badge
                            badgeClasses="badge-danger"
                            badgeText="Auto-flags Issue"
                            badgeIcon={<IconFlag />}
                        />
                    )}
                </div>
                <div className="table-actions">
                    <BtnIconOnly
                        size="sm"
                        variant="danger"
                        buttonClasses="item-card-delete"
                        icon={<IconTrash />}
                        ariaLabel="Delete item"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDeletePromptModal(true);
                            setDeletePromptInSectionIndex({ sectionIndex, promptIndex });
                        }}
                    />
                    <BtnIconOnly
                        size="sm"
                        buttonClasses="item-card-expand"
                        icon={isExpanded ? <IconAngleUp /> : <IconAngleDown />}
                        ariaLabel={isExpanded ? 'Collapse item' : 'Expand item'}
                        buttonAriaProps={{ 'aria-expanded': isExpanded }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedItemIndex(
                                isExpanded ? null : { sectionIndex, promptIndex },
                            );
                        }}
                    />
                </div>
            </div>
        );
    };

    /* ─── Render expanded item card (daily service) ─── */
    const renderExpandedItemCard = (prompt: tPrompt, sectionIndex: number, promptIndex: number) => {
        if (isDailyServiceProcedurePrompt(prompt)) {
            const helper =
                (prompt.serviceProcedureId &&
                    procedureConsumablesHelperText(prompt.serviceProcedureId, procedureLinks, serviceItems)) ||
                prompt.description ||
                '';
            return (
                <div className="item-card-expanded">
                    <div className="item-details-section">
                        <h4 className="item-section-label">Procedure</h4>
                        <p className="item-section-hint">
                            Acknowledgement only on the Daily Service log — not required. Linked consumables use the
                            flat-rate quantity from Resources.
                        </p>
                        {helper && <p className="item-section-hint">{helper}</p>}
                        {!prompt.serviceProcedureId && (
                            <p className="item-section-hint">
                                Choose an existing procedure (this heading lists first) or use + Add new procedure.
                                New procedures save to Resources without leaving the form.
                            </p>
                        )}
                    </div>
                    {renderPromptActions(sectionIndex, promptIndex)}
                </div>
            );
        }

        const usesCatalogSuggest = itemUsesCatalogSuggest(prompt, formContents[sectionIndex]);
        const sectionCategory =
            (formContents[sectionIndex] as tSectionContent | undefined)?.name || '';

        const persistListingFields = async (
            overrides?: { partNumber?: string; vendorName?: string; manufacturer?: string },
            vendorList = vendors,
        ) => {
            const patch: Partial<tPrompt> = {};
            if (overrides?.partNumber !== undefined) patch.partNumber = overrides.partNumber;
            if (overrides?.manufacturer !== undefined) patch.partDescription = overrides.manufacturer;
            if (overrides?.vendorName !== undefined) patch.vendor = overrides.vendorName;
            if (Object.keys(patch).length) {
                const contents = formContentsRef.current;
                const section = contents[sectionIndex];
                const current = section?.prompts?.[promptIndex];
                if (section?.prompts && current) {
                    const prompts = [...section.prompts];
                    prompts[promptIndex] = { ...current, ...patch };
                    const next = [...contents];
                    next[sectionIndex] = { ...section, prompts } as tFormPrompts;
                    formContentsRef.current = next;
                    updatePromptFields(sectionIndex, promptIndex, patch);
                }
            }
            await catalogSaveInFlightRef.current.get(`${sectionIndex}:${promptIndex}`);
            const latest = formContentsRef.current[sectionIndex]?.prompts?.[promptIndex] ?? prompt;
            if (!latest.serviceItemId && !latest.component?.trim()) return;
            const item = await persistPromptListingToCatalog(
                latest,
                sectionCategory,
                vendorList,
                overrides,
            );
            if (item) applyCatalogItemToPrompt(sectionIndex, promptIndex, item);
        };

        const itemFieldId = prompt.clientId || prompt.serviceItemId || `${sectionIndex}-${promptIndex}`;
        const categoryOptions = prompt.category &&
            !DAILY_SERVICE_ITEM_CATEGORY_OPTIONS.some((option) => option.value === prompt.category)
            ? [
                  ...DAILY_SERVICE_ITEM_CATEGORY_OPTIONS,
                  ...FORM_ITEM_CATEGORY_OPTIONS.filter((option) => option.value === prompt.category),
              ]
            : DAILY_SERVICE_ITEM_CATEGORY_OPTIONS;
        const responseOptions = prompt.operatorResponseType &&
            !DAILY_SERVICE_OPERATOR_RESPONSE_TYPE_OPTIONS.some(
                (option) => option.value === prompt.operatorResponseType,
            )
            ? [
                  ...DAILY_SERVICE_OPERATOR_RESPONSE_TYPE_OPTIONS,
                  ...OPERATOR_RESPONSE_TYPE_OPTIONS.filter(
                      (option) => option.value === prompt.operatorResponseType,
                  ),
              ]
            : DAILY_SERVICE_OPERATOR_RESPONSE_TYPE_OPTIONS;

        return (
            <div className="item-card-expanded">
                <div className="item-details-section">
                    <h4 className="item-section-label">Item</h4>
                    {usesCatalogSuggest && (
                        <p className="item-section-hint">
                            Filled from the item you chose above. New items save to Resources without leaving the form.
                        </p>
                    )}
                    <div className="item-details-grid">
                        <div className="form-field">
                            <label htmlFor={`ds-item-${itemFieldId}-description`}>Description</label>
                            {usesCatalogSuggest ? (
                                <input
                                    id={`ds-item-${itemFieldId}-description`}
                                    type="text"
                                    readOnly
                                    value={prompt.component || ''}
                                    placeholder="Choose an item above"
                                />
                            ) : (
                                <input
                                    id={`ds-item-${itemFieldId}-description`}
                                    type="text"
                                    value={prompt.component || ''}
                                    placeholder="e.g. Fuel consumed (diesel)"
                                    onChange={(e) =>
                                        updatePromptField(sectionIndex, promptIndex, 'component', e.target.value)
                                    }
                                />
                            )}
                        </div>
                        <div className="form-field">
                            <label htmlFor={`ds-item-${itemFieldId}-manufacturer`}>Manufacturer</label>
                            <input
                                id={`ds-item-${itemFieldId}-manufacturer`}
                                type="text"
                                value={prompt.partDescription || ''}
                                placeholder="e.g. CAT"
                                onChange={(e) =>
                                    updatePromptField(sectionIndex, promptIndex, 'partDescription', e.target.value)
                                }
                                onBlur={(e) => {
                                    void persistListingFields({ manufacturer: e.target.value });
                                }}
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor={`ds-item-${itemFieldId}-part`}>Part #</label>
                            <input
                                id={`ds-item-${itemFieldId}-part`}
                                type="text"
                                value={prompt.partNumber || ''}
                                placeholder="e.g. P001357"
                                onChange={(e) =>
                                    updatePromptField(sectionIndex, promptIndex, 'partNumber', e.target.value)
                                }
                                onBlur={(e) => {
                                    void persistListingFields({ partNumber: e.target.value });
                                }}
                            />
                        </div>
                        <div className="form-field">
                            <label htmlFor={`ds-item-${itemFieldId}-vendor`}>Vendor</label>
                            <SearchSuggest
                                inputId={`ds-item-${itemFieldId}-vendor`}
                                ariaLabel="Vendor"
                                placeholder="Search vendors..."
                                options={sortVendorsByName(vendors)
                                    .filter((vendor: { name?: string | null }) => Boolean(vendor.name?.trim()))
                                    .map((vendor: { name?: string | null }) => ({
                                        value: vendor.name || '',
                                        label: vendor.name || '',
                                    }))}
                                value={prompt.vendor || ''}
                                onChange={(vendorName) => {
                                    updatePromptField(sectionIndex, promptIndex, 'vendor', vendorName);
                                    void persistListingFields({ vendorName });
                                }}
                                actionOption={{
                                    label: '+ Add new vendor',
                                    onClick: ({ searchText }) => {
                                        setVendorDraftName(searchText.trim());
                                        setVendorPromptTarget({ sectionIndex, promptIndex });
                                        setShowVendorModal(true);
                                    },
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="item-details-section">
                    <div className="form-field">
                        <label htmlFor={`ds-item-${itemFieldId}-helper`}>Description / Helper Text</label>
                        <input
                            id={`ds-item-${itemFieldId}-helper`}
                            type="text"
                            value={prompt.description || ''}
                            placeholder="e.g. Record gallons filled"
                            onChange={(e) =>
                                updatePromptField(sectionIndex, promptIndex, 'description', e.target.value)
                            }
                        />
                    </div>
                </div>

                <FormCategory
                    value={prompt.category}
                    options={categoryOptions}
                    onChange={(val) =>
                        updatePromptFields(sectionIndex, promptIndex, {
                            category: val,
                            ...promptPatchForDailyServiceCategory(val),
                        })
                    }
                />

                <FormOperatorResponseType
                    value={prompt.operatorResponseType}
                    options={responseOptions}
                    onChange={(val) =>
                        updatePromptField(sectionIndex, promptIndex, 'operatorResponseType', val)
                    }
                />

                <div className="item-details-section">
                    <h4 className="item-section-label">Quantity & Unit of Measure</h4>
                    <div className="form-field form-field-inline default-um-field">
                        <label htmlFor={`ds-item-um-${itemFieldId}`}>
                            Default UM
                        </label>
                        <div className="default-um-select-fit">
                            <UnitOfMeasureSearchSuggest
                                className="default-um-select"
                                value={prompt.defaultUM || ''}
                                onChange={(val) =>
                                    updatePromptField(sectionIndex, promptIndex, 'defaultUM', val)
                                }
                                extraValues={prompt.defaultUM ? [prompt.defaultUM] : []}
                                inputId={`ds-item-um-${itemFieldId}`}
                                ariaLabel="Default UM"
                                placeholder="Search or add UM"
                            />
                        </div>
                    </div>
                </div>

                <Card
                    variant="card-sm"
                    className={`autoflag-card${prompt.autoFlag ? ' is-on' : ''}`}
                    role="switch"
                    tabIndex={0}
                    aria-checked={Boolean(prompt.autoFlag)}
                    aria-label="Auto-flag as Issue when triggered"
                    onClick={() =>
                        updatePromptField(sectionIndex, promptIndex, 'autoFlag', !prompt.autoFlag)
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            updatePromptField(sectionIndex, promptIndex, 'autoFlag', !prompt.autoFlag);
                        }
                    }}
                >
                    <div className="autoflag-toggle">
                        <div className="autoflag-copy">
                            <span className="toggle-text">Auto-flag as Issue when triggered</span>
                            <span className="toggle-hint">
                                {prompt.operatorResponseType === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY
                                    ? 'Creates an issue and sends email when the operator enters a quantity'
                                    : 'Creates an issue record automatically when operator reports this item'}
                            </span>
                        </div>
                        <span
                            className={`autoflag-switch ${prompt.autoFlag ? 'is-on' : ''}`}
                            aria-hidden="true"
                        >
                            <span className="autoflag-switch-thumb" />
                        </span>
                    </div>
                </Card>

                {renderPromptActions(sectionIndex, promptIndex)}
            </div>
        );
    };

    /* ─── Render inspection/shift form prompt content ─── */
    const renderInspectionPromptContent = (prompt: tPrompt, sectionIndex: number, promptIndex: number) => {
        // Resolve response type — old prompts may have type undefined (defaults to fourResponse)
        const responseType = prompt.operatorResponseType || prompt.type || OPERATOR_RESPONSE_TYPES.FOUR_BUTTON;

        return (
            <div className="inspection-prompt-card">
                <div className="inspection-prompt-fields">
                    <div className="form-field">
                        <label>What are you inspecting?</label>
                        <input
                            type="text"
                            placeholder="e.g. Hydraulic System"
                            value={prompt.component || ''}
                            onChange={(e) => updatePromptField(sectionIndex, promptIndex, 'component', e.target.value)}
                        />
                    </div>
                    <div className="form-field">
                        <label>What are you looking for?</label>
                        <input
                            type="text"
                            placeholder="e.g. Broken lamps, lenses, operation"
                            value={prompt.description || ''}
                            onChange={(e) =>
                                updatePromptField(sectionIndex, promptIndex, 'description', e.target.value)
                            }
                        />
                    </div>
                </div>

                <div className="inspection-prompt-type-row">
                    <div className="form-field response-type-field">
                        <Select
                            ariaLabel="Response type"
                            value={responseType}
                            options={inspectionResponseTypeOptions(responseType)}
                            onChange={(val) => {
                                const isSharedResponseType = (
                                    Object.values(OPERATOR_RESPONSE_TYPES) as string[]
                                ).includes(val);
                                if (isSharedResponseType) {
                                    updatePromptField(sectionIndex, promptIndex, 'operatorResponseType', val);
                                    updatePromptField(sectionIndex, promptIndex, 'type', val);
                                } else {
                                    updatePromptField(sectionIndex, promptIndex, 'type', val);
                                    updatePromptField(sectionIndex, promptIndex, 'operatorResponseType', undefined);
                                }
                            }}
                        />
                    </div>
                    {renderPromptActions(sectionIndex, promptIndex)}
                </div>

                {/* Mini button preview showing what the inspector will see */}
                {responseType === OPERATOR_RESPONSE_TYPES.FOUR_BUTTON && (
                    <div className="response-preview">
                        <span className="preview-btn preview-good">Good</span>
                        <span className="preview-btn preview-warn">Warning</span>
                        <span className="preview-btn preview-fail">Fail</span>
                        <span className="preview-btn preview-na">N/A</span>
                    </div>
                )}
                {responseType === OPERATOR_RESPONSE_TYPES.ADDED_NOT_ADDED && (
                    <div className="response-preview">
                        <span className="preview-btn preview-good">Added</span>
                        <span className="preview-btn preview-na">Not Added</span>
                    </div>
                )}
                {responseType === OPERATOR_RESPONSE_TYPES.OK_CLEANED_REPLACED_NA && (
                    <div className="response-preview">
                        <span className="preview-btn preview-good">OK</span>
                        <span className="preview-btn preview-warn">Cleaned</span>
                        <span className="preview-btn preview-warn">Replaced</span>
                        <span className="preview-btn preview-na">N/A</span>
                    </div>
                )}
                {responseType === OPERATOR_RESPONSE_TYPES.OK_REPLACED_NA && (
                    <div className="response-preview">
                        <span className="preview-btn preview-good">OK</span>
                        <span className="preview-btn preview-warn">Replaced</span>
                        <span className="preview-btn preview-na">N/A</span>
                    </div>
                )}
                {responseType === OPERATOR_RESPONSE_TYPES.YES_NO && (
                    <div className="response-preview">
                        <span className="preview-btn preview-good">Yes</span>
                        <span className="preview-btn preview-fail">No</span>
                    </div>
                )}
                {responseType === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY && (
                    <div className="response-preview">
                        <span className="preview-hint">Inspector enters a number directly</span>
                    </div>
                )}

                {/* Options for checkbox/dropdown */}
                {(responseType === DEFAULT_FORM_INPUT_TYPES.checkbox ||
                    responseType === DEFAULT_FORM_INPUT_TYPES.dropdown) && (
                    <div className="prompt-options-section">
                        <label>
                            {responseType === DEFAULT_FORM_INPUT_TYPES.checkbox
                                ? 'Checkbox Options'
                                : 'Dropdown Options'}
                        </label>
                        {Array.isArray(prompt.options) &&
                            prompt.options.map((option, optionIndex) => (
                                <div key={optionIndex} className="option-row">
                                    <input
                                        type="text"
                                        placeholder="Option text"
                                        value={option}
                                        onChange={(e) => {
                                            setFormContents((prev) => {
                                                const nc = [...prev];
                                                const sec = nc[sectionIndex];
                                                if (sec.prompts) {
                                                    const up = [...sec.prompts];
                                                    const cp = {
                                                        ...up[promptIndex],
                                                        options: [...(up[promptIndex].options || [])],
                                                    };
                                                    cp.options![optionIndex] = e.target.value;
                                                    up[promptIndex] = cp;
                                                    nc[sectionIndex] = { ...sec, prompts: up } as tFormPrompts;
                                                }
                                                return nc;
                                            });
                                        }}
                                    />
                                    <BtnIconOnly
                                        size="sm"
                                        variant="danger"
                                        icon={<IconTrash />}
                                        ariaLabel="Remove option"
                                        onClick={() => {
                                            setFormContents((prev) => {
                                                const nc = [...prev];
                                                const sec = nc[sectionIndex];
                                                if (sec.prompts) {
                                                    const up = [...sec.prompts];
                                                    up[promptIndex] = {
                                                        ...up[promptIndex],
                                                        options: (up[promptIndex].options || []).filter(
                                                            (_, i) => i !== optionIndex,
                                                        ),
                                                    };
                                                    nc[sectionIndex] = { ...sec, prompts: up } as tFormPrompts;
                                                }
                                                return nc;
                                            });
                                        }}
                                    />
                                </div>
                            ))}
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="+ Add Option"
                            buttonOnClick={() => {
                                setFormContents((prev) => {
                                    const nc = [...prev];
                                    const sec = nc[sectionIndex];
                                    if (sec.prompts) {
                                        const up = [...sec.prompts];
                                        up[promptIndex] = {
                                            ...up[promptIndex],
                                            options: [...(up[promptIndex].options || []), ''],
                                        };
                                        nc[sectionIndex] = { ...sec, prompts: up } as tFormPrompts;
                                    }
                                    return nc;
                                });
                            }}
                        />
                    </div>
                )}

                {/* Input mode for sectionQuestion */}
                {responseType === DEFAULT_FORM_INPUT_TYPES.sectionQuestion && (
                    <div className="form-field" style={{ marginTop: '8px' }}>
                        <label>Input Type</label>
                        <Select
                            ariaLabel="Input Type"
                            value={prompt.inputMode?.value || 'text'}
                            options={FORM_INPUT_MODE_OPTIONS}
                            onChange={(value) => {
                                const label =
                                    FORM_INPUT_MODE_OPTIONS.find((option) => option.value === value)?.label || value;
                                updatePromptField(sectionIndex, promptIndex, 'inputMode', {
                                    label,
                                    value,
                                });
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    /* ─── Render standalone question ─── */
    const renderStandaloneQuestion = (content: tFormPrompts, index: number, dragHandleProps: Record<string, any>) => (
        <div className="standalone-question-card">
            <DragHandleIcon {...dragHandleProps} />
            <div className="standalone-question-fields">
                <div className="form-field">
                    <label>What are you asking about?</label>
                    <input
                        type="text"
                        placeholder="e.g. General Condition"
                        value={content.prompt || ''}
                        onChange={(e) => {
                            setFormContents([
                                ...formContents.slice(0, index),
                                { ...formContents[index], prompt: e.target.value },
                                ...formContents.slice(index + 1),
                            ]);
                        }}
                    />
                </div>
                <div className="form-field">
                    <label>Type of Input</label>
                    <Select
                        ariaLabel="Type of Input"
                        value={content.inputMode?.value || 'text'}
                        options={FORM_INPUT_MODE_OPTIONS}
                        onChange={(value) => {
                            const label =
                                FORM_INPUT_MODE_OPTIONS.find((option) => option.value === value)?.label || value;
                            setFormContents((prev) => {
                                const nc = [...prev];
                                nc[index] = {
                                    ...prev[index],
                                    inputMode: { label, value },
                                } as tFormPrompts;
                                return nc;
                            });
                        }}
                    />
                </div>
            </div>
            <BtnIconOnly
                size="sm"
                variant="danger"
                icon={<IconTrash />}
                ariaLabel="Delete question"
                onClick={() => {
                    setShowDeletePromptModal(true);
                    setDeletePromptIndex(index);
                }}
            />
        </div>
    );

    /* ─── Render section ─── */
    const renderSection = (
        content: {
            prompt?: string;
            type?: string;
            name: string;
            inputMode?: { label: string; value: string };
            prompts: tPrompt[];
        },
        sectionIndex: number,
        dragHandleProps: Record<string, any>,
    ) => {
        const isCollapsed = collapsedSections.has(sectionIndex);
        const sectionNumber = formContents
            .slice(0, sectionIndex + 1)
            .filter((c) => c.type === DEFAULT_FORM_INPUT_TYPES.section).length;
        const totalSections = formContents.filter((c) => c.type === DEFAULT_FORM_INPUT_TYPES.section).length;
        const promptIds = content.prompts.map((_, i) => `prompt-${sectionIndex}-${i}`);
        const catalogKind = resolveSectionCatalogKind(formContents[sectionIndex] || content);
        const catalogPickerKey = sectionCatalogChangeKey(content, sectionIndex);
        if (catalogKind) {
            lastCatalogKindBySectionRef.current[catalogPickerKey] = catalogKind;
        }
        const isChangingCatalog = changingCatalogKeys.has(catalogPickerKey);
        const headingOptions = catalogKind
            ? headingNamesForCatalog(
                  catalogKind,
                  serviceItems,
                  serviceProcedures,
                  extraServiceCategories,
              ).map((name) => ({ value: name, label: name }))
            : [];
        const isProcedureSection = catalogKind === DS_SECTION_CATALOG.PROCEDURES;
        const headingPlaceholder = isProcedureSection
            ? 'Search or add Procedure category'
            : 'Search or add Service Item category';
        const canChangeCatalog = usesCatalogSuggest(content) && (content.prompts || []).length === 0;
        const isFirstSectionEmpty = sectionIndex === 0 && (content.prompts || []).length === 0;
        let emptySectionHint: string | null = null;
        if (isFirstSectionEmpty) {
            if (!isDailyService) {
                emptySectionHint = 'This section is empty. Add a question below.';
            } else if (!catalogKind) {
                emptySectionHint =
                    'Choose Service Items or Service Procedures in the header, then add your first item here.';
            } else if (isProcedureSection) {
                emptySectionHint = 'This section is empty. Add a procedure below.';
            } else {
                emptySectionHint = 'This section is empty. Add a service item below.';
            }
        }

        return (
            <div className="section-wrapper">
                <div className="section-badge">
                    Section {sectionNumber} of {totalSections}
                </div>
                <CollapsibleCard
                    className="form-section-card"
                    dragHandleProps={dragHandleProps}
                    expanded={!isCollapsed}
                    onExpandedChange={(expanded) => {
                        setCollapsedSections((prev) => {
                            const next = new Set(prev);
                            if (expanded) next.delete(sectionIndex);
                            else next.add(sectionIndex);
                            return next;
                        });
                    }}
                    title={
                        usesCatalogSuggest(content) ? (
                            catalogKind && !isChangingCatalog ? (
                                <div className="ds-section-heading-wrap" onClick={(event) => event.stopPropagation()}>
                                    <SearchSuggest
                                        className="collapsible-card-title-suggest"
                                        options={headingOptions}
                                        value={content.name || ''}
                                        onChange={(value) => handleSectionCategoryChange(sectionIndex, value)}
                                        placeholder={headingPlaceholder}
                                        emptyMessage="No matching headings"
                                        actionOption={{
                                            label: '+ Add new category',
                                            onClick: ({ searchText }) =>
                                                handleAddSectionCategory(sectionIndex, searchText),
                                        }}
                                        inputId={`ds-section-category-${sectionIndex}`}
                                        ariaLabel={
                                            isProcedureSection
                                                ? 'Procedure category heading'
                                                : 'Service Item category heading'
                                        }
                                    />
                                    {canChangeCatalog && (
                                        <Btn
                                            variant="ghost"
                                            buttonSize="sm"
                                            buttonText="Change catalog"
                                            buttonOnClick={() => beginCatalogChange(sectionIndex, content)}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div
                                    className="ds-section-catalog-picker"
                                    role="group"
                                    aria-label="Section catalog"
                                    aria-describedby={`ds-section-catalog-hint-${sectionIndex}`}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <p
                                        id={`ds-section-catalog-hint-${sectionIndex}`}
                                        className="item-section-hint"
                                    >
                                        Choose a catalog for this section. Catalog changes stay on this form.
                                    </p>
                                    <div className="ds-section-catalog-picker-actions">
                                        <Btn
                                            variant="ghost"
                                            buttonSize="sm"
                                            buttonText="Service Items"
                                            buttonOnClick={() =>
                                                commitCatalogKind(
                                                    sectionIndex,
                                                    content,
                                                    DS_SECTION_CATALOG.SERVICE_ITEMS,
                                                )
                                            }
                                        />
                                        <Btn
                                            variant="ghost"
                                            buttonSize="sm"
                                            buttonText="Service Procedures"
                                            buttonOnClick={() =>
                                                commitCatalogKind(
                                                    sectionIndex,
                                                    content,
                                                    DS_SECTION_CATALOG.PROCEDURES,
                                                )
                                            }
                                        />
                                        <Btn
                                            variant="ghost"
                                            buttonSize="sm"
                                            buttonText="Cancel"
                                            buttonOnClick={() => cancelCatalogChange(sectionIndex, content)}
                                        />
                                    </div>
                                </div>
                            )
                        ) : (
                            <input
                                type="text"
                                className="collapsible-card-title-input"
                                placeholder="Section Name (e.g. Engine & Fluids)"
                                aria-label="Section name"
                                value={content.name || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    setFormContents([
                                        ...formContents.slice(0, sectionIndex),
                                        {
                                            type: DEFAULT_FORM_INPUT_TYPES.section,
                                            name: e.target.value,
                                            prompts: content.prompts,
                                        },
                                        ...formContents.slice(sectionIndex + 1),
                                    ]);
                                }}
                            />
                        )
                    }
                    meta={
                        catalogKind
                            ? `${isProcedureSection ? 'Procedures' : 'Service Items'} · ${content.prompts.length} item${content.prompts.length !== 1 ? 's' : ''}`
                            : `${content.prompts.length} item${content.prompts.length !== 1 ? 's' : ''}`
                    }
                    actions={
                        <div className="table-actions">
                            <BtnIconOnly
                                size="sm"
                                fill="opaque"
                                icon={<IconActions />}
                                ariaLabel="Section options"
                                onClick={() => {
                                    /* 3-dot menu - move up/down/duplicate */
                                }}
                            />
                            <BtnIconOnly
                                size="sm"
                                fill="opaque"
                                variant="danger"
                                icon={<IconTrash />}
                                ariaLabel="Delete section"
                                onClick={() => {
                                    setShowDeleteSectionModal(true);
                                    setDeleteSectionIndex(sectionIndex);
                                }}
                            />
                        </div>
                    }
                >
                    {emptySectionHint ? (
                        <p className="item-section-hint form-section-empty-hint">{emptySectionHint}</p>
                    ) : null}
                    <DndContext
                        sensors={promptSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handlePromptDragEnd(sectionIndex)}
                    >
                        <SortableContext items={promptIds} strategy={verticalListSortingStrategy}>
                            <div className="section-prompts">
                                {content.prompts.map((prompt, promptIndex) => {
                                    const isItemExpanded =
                                        expandedItemIndex?.sectionIndex === sectionIndex &&
                                        expandedItemIndex?.promptIndex === promptIndex;
                                    const promptKey =
                                        prompt.clientId ||
                                        prompt.serviceItemId ||
                                        `prompt-${sectionIndex}-${promptIndex}`;
                                    return (
                                        <SortablePrompt
                                            key={promptKey}
                                            id={`prompt-${sectionIndex}-${promptIndex}`}
                                        >
                                            {isDailyService
                                                ? (dragHandleProps) => (
                                                    <div
                                                        className={[
                                                            'item-card',
                                                            getCategoryItemCardClass(prompt.category),
                                                            isItemExpanded ? 'is-expanded' : '',
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                        data-ds-item-card={`${sectionIndex}-${promptIndex}`}
                                                    >
                                                        {renderCollapsedItemCard(
                                                            prompt,
                                                            sectionIndex,
                                                            promptIndex,
                                                            dragHandleProps,
                                                        )}
                                                        {isItemExpanded &&
                                                            renderExpandedItemCard(
                                                                prompt,
                                                                sectionIndex,
                                                                promptIndex,
                                                            )}
                                                    </div>
                                                )
                                                : renderInspectionPromptContent(
                                                    prompt,
                                                    sectionIndex,
                                                    promptIndex,
                                                )}
                                        </SortablePrompt>
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>

                    {isDailyService ? (
                        catalogKind ? (
                            <div className="btn-add-item-row">
                                {isProcedureSection ? (
                                    <Btn
                                        variant="outlined-faint"
                                        buttonClasses="btn-add-item"
                                        buttonIcon={<IconPlus />}
                                        buttonText={`Add Procedure to ${content.name || 'Section'}`}
                                        buttonOnClick={() =>
                                            addPromptToSection(sectionIndex, createDailyServiceProcedurePrompt())
                                        }
                                    />
                                ) : (
                                    <Btn
                                        variant="outlined-faint"
                                        buttonClasses="btn-add-item"
                                        buttonIcon={<IconPlus />}
                                        buttonText={`Add Item to ${content.name || 'Section'}`}
                                        buttonOnClick={() =>
                                            addPromptToSection(sectionIndex, createDailyServicePrompt(true))
                                        }
                                    />
                                )}
                            </div>
                        ) : null
                    ) : (
                        <Btn
                            variant="outlined-faint"
                            buttonClasses="btn-add-item"
                            buttonIcon={<IconPlus />}
                            buttonText={`Add Item to ${content.name || 'Section'}`}
                            buttonOnClick={() => {
                                const newPrompt: tPrompt = {
                                    type: OPERATOR_RESPONSE_TYPES.FOUR_BUTTON,
                                    operatorResponseType: OPERATOR_RESPONSE_TYPES.FOUR_BUTTON,
                                    component: '',
                                    description: '',
                                };
                                setFormContents((prev) => {
                                    const nc = [...prev];
                                    const sec = nc[sectionIndex];
                                    nc[sectionIndex] = {
                                        ...sec,
                                        prompts: [...(sec.prompts || []), newPrompt],
                                    } as tFormPrompts;
                                    return nc;
                                });
                            }}
                        />
                    )}
                </CollapsibleCard>
            </div>
        );
    };

    const renderElement = (
        content: tQuestionContent | tSectionContent,
        index: number,
        dragHandleProps: Record<string, any>,
    ) => {
        switch (content.type) {
            case DEFAULT_FORM_INPUT_TYPES.standAloneQuestion:
                return renderStandaloneQuestion(content as tFormPrompts, index, dragHandleProps);
            case DEFAULT_FORM_INPUT_TYPES.section:
                return renderSection(content as tSectionContent, index, dragHandleProps);
        }
    };

    const topLevelIds = formContents.map((_, i) => `item-${i}`);
    const hasSections = formContents.some((c) => c.type === DEFAULT_FORM_INPUT_TYPES.section);

    // Footer stats
    const totalItems = formContents.reduce(
        (acc, c) => acc + (c.prompts?.length || (c.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion ? 1 : 0)),
        0,
    );
    const totalSections = formContents.filter((c) => c.type === DEFAULT_FORM_INPUT_TYPES.section).length;
    const totalAutoFlag = formContents.reduce((acc, c) => {
        if (c.prompts) return acc + c.prompts.filter((p) => p.autoFlag).length;
        return acc;
    }, 0);

    const trimmedFormName = formName.trim();
    const resolvedPageTitle =
        pageTitle ||
        (isFormCopy
            ? trimmedFormName
                ? `Copy Form ${trimmedFormName}`
                : 'Copy Form'
            : isNewForm
              ? 'Create a Form'
              : isEditMode
                ? 'Edit Form'
                : 'Edit Template');
    const formTypeSubtitle =
        formTypeSelection === FORM_TYPES.DAILY_SERVICE
            ? 'Daily Service'
            : formTypeSelection === FORM_TYPES.SHIFT_REPORT
              ? 'Shift Form'
              : 'Inspection Form';
    const headerFormTypeSubtitle = trimmedFormName
        ? `${formTypeSubtitle} : ${trimmedFormName}`
        : formTypeSubtitle;
    const showFormTypePicker = !lockFormType && resolvedAllowedFormTypes.length > 1;

    return (
        <div
            className={
                isOverlay
                    ? 'form-builder-root form-builder-root--overlay'
                    : 'form-builder-root page-container single-form-page'
            }
        >
            {!isOverlay && (
                <>
                    <PageHeader
                        as="h1"
                        title={resolvedPageTitle}
                        subtitle={headerFormTypeSubtitle}
                        subtitleClassName={`form-type-badge ${formTypeSelection}${trimmedFormName ? ' form-type-badge--named' : ''}`}
                        actions={
                            <>
                                {showCreateFormCopyAction ? (
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonText="Create a new copy"
                                        buttonOnClick={handleCreateFormCopy}
                                        buttonDisabled={isCreating}
                                    />
                                ) : null}
                                {showPublishAction ? (
                                    <button
                                        type="button"
                                        className="btn-publish"
                                        disabled={!canPublish}
                                        onClick={() => handleSubmit(undefined, 'published')}
                                        title={formValidationHint || undefined}
                                    >
                                        <span className="btn-publish-label">
                                            <span>{isCreating ? 'Saving...' : publishButtonLabel}</span>
                                            <span className="btn-publish-label-sizer" aria-hidden="true">
                                                {publishButtonLabel}
                                            </span>
                                        </span>
                                    </button>
                                ) : null}
                            </>
                        }
                        breadcrumbs={[
                            {
                                label: 'Forms',
                                href: formsListHref,
                                onClick: () => handleBackClick(),
                            },
                            {
                                label: resolvedPageTitle,
                                href: undefined,
                            },
                        ]}
                    />
                    <BackButton to={formsListHref} onClick={handleBackClick} buttonText="Back" ariaLabel="Back to forms" />
                </>
            )}

            <Card variant="card-xl" className="card-pad-0 form-builder-card">
                {/* Form Settings Bar */}
                <div className="form-content">
                    <form
                        className="form-builder"
                        onSubmit={(e) => {
                            // Prevent Enter-in-input from auto-publishing (default submit).
                            e.preventDefault();
                        }}
                    >
                        <div className="form-config-grid">
                            <div className="form-config-left">
                                {isDailyService ? (
                                    <>
                                        <div className="form-field">
                                            <label htmlFor="form-equipment-category">Asset/Equipment Category</label>
                                            <SearchSuggest
                                                inputId="form-equipment-category"
                                                options={sortedCategoryOptions}
                                                value={equipmentCategorySelectValue(equipmentType)}
                                                onChange={(next) =>
                                                    setEquipmentType(equipmentCategoryStoredName(next))
                                                }
                                                placeholder="Search equipment categories..."
                                                emptyMessage="No matching categories"
                                                actionOption={{
                                                    label: ADD_EQUIPMENT_TYPE_OPTION_LABEL,
                                                    onClick: ({ searchText }) => {
                                                        setPendingCategorySearchText(searchText.trim());
                                                        setShowCategoryOverlay(true);
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label htmlFor="form-name">Form Name</label>
                                            <SearchSuggest
                                                inputId="form-name"
                                                options={formNameOptions}
                                                value={formName}
                                                onChange={setFormName}
                                                allowNew
                                                newLabel={ADD_FORM_NAME_LABEL}
                                                placeholder="Search or add a form name..."
                                                emptyMessage="No matching form names"
                                                hasError={Boolean(duplicateFormName)}
                                                ariaDescribedBy={duplicateFormName ? 'form-name-error' : undefined}
                                            />
                                            {duplicateFormName && (
                                                <p className="form-field-error" id="form-name-error" role="alert">
                                                    {duplicateFormNameMessage(formName)}
                                                </p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="form-field">
                                            <label htmlFor="form-name">Form Name</label>
                                            <input
                                                id="form-name"
                                                type="text"
                                                value={formName}
                                                onChange={(e) => setFormName(e.target.value)}
                                                placeholder="Enter form name"
                                                required
                                            />
                                            {duplicateFormName && (
                                                <p className="form-field-error" id="form-name-error" role="alert">
                                                    {duplicateFormNameMessage(formName)}
                                                </p>
                                            )}
                                        </div>
                                        <div className="form-field">
                                            <label>Asset/Equipment Category</label>
                                            <Select
                                                options={sortedCategoryOptions}
                                                value={equipmentCategorySelectValue(equipmentType)}
                                                onChange={(next) =>
                                                    setEquipmentType(equipmentCategoryStoredName(next))
                                                }
                                                placeholder="All Equipment"
                                                ariaLabel="Asset/Equipment Category"
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="form-field">
                                    <label htmlFor="form-um-counter-label">Meter (UM) Label</label>
                                    <UnitOfMeasureSearchSuggest
                                        value={umCounterLabel}
                                        onChange={setUmCounterLabel}
                                        extraValues={umCounterLabel ? [umCounterLabel] : []}
                                        inputId="form-um-counter-label"
                                        ariaLabel="Meter (UM) Label"
                                        placeholder="Search or add UM"
                                    />
                                </div>
                            </div>
                            <div className="form-config-right">
                                {showFormTypePicker ? (
                                    <div className="form-field">
                                        <label>Form Type</label>
                                        <div className="radio-group horizontal">
                                            {resolvedAllowedFormTypes.includes(FORM_TYPES.INSPECTION) && (
                                                <div className="radio-option">
                                                    <input
                                                        type="radio"
                                                        id="inspectionForm"
                                                        name="formType"
                                                        value={FORM_TYPES.INSPECTION}
                                                        checked={formTypeSelection === FORM_TYPES.INSPECTION}
                                                        onChange={() => handleFormTypeChange(FORM_TYPES.INSPECTION)}
                                                    />
                                                    <label htmlFor="inspectionForm">Inspection Form</label>
                                                </div>
                                            )}
                                            {resolvedAllowedFormTypes.includes(FORM_TYPES.DAILY_SERVICE) && (
                                                <div className="radio-option">
                                                    <input
                                                        type="radio"
                                                        id="dailyServiceForm"
                                                        name="formType"
                                                        value={FORM_TYPES.DAILY_SERVICE}
                                                        checked={formTypeSelection === FORM_TYPES.DAILY_SERVICE}
                                                        onChange={() => handleFormTypeChange(FORM_TYPES.DAILY_SERVICE)}
                                                    />
                                                    <label htmlFor="dailyServiceForm">Daily Service Log Form</label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="form-field">
                                        <label>Form Type</label>
                                        <div className={`form-type-badge ${formTypeSelection}`}>{formTypeSubtitle}</div>
                                    </div>
                                )}
                                <div className="checkbox-field">
                                    <input
                                        type="checkbox"
                                        id="alwaysEmail"
                                        checked={alwaysTriggerEmail}
                                        onChange={(e) => setAlwaysTriggerEmail(e.target.checked)}
                                    />
                                    <label htmlFor="alwaysEmail">Check this box if you want an email notification of completion</label>
                                </div>
                            </div>
                        </div>

                        <div className="form-sections">
                            {/* Collapse/Expand controls */}
                            {hasSections && (
                                <div className="collapse-controls">
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonSize="sm"
                                        buttonText="Collapse All"
                                        buttonOnClick={collapseAll}
                                    />
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonSize="sm"
                                        buttonText="Expand All"
                                        buttonOnClick={expandAll}
                                    />
                                </div>
                            )}

                            {/* Form Questions - drag & drop */}
                            <div className="form-questions-wrapper">
                                <div className="form-questions">
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleTopLevelDragEnd}
                                    >
                                        <SortableContext items={topLevelIds} strategy={verticalListSortingStrategy}>
                                            {formContents.map((content, index) => (
                                                <SortableItem key={`item-${index}`} id={`item-${index}`}>
                                                    {(dragHandleProps) =>
                                                        renderElement(content, index, dragHandleProps)
                                                    }
                                                </SortableItem>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            </div>

                            {/* Add Buttons (below content) */}
                            <div className="add-buttons">
                                {!isDailyService && (
                                    <button type="button" className="btn-add" onClick={addQuestion}>
                                        + Add A Question
                                    </button>
                                )}
                                {isDailyService ? (
                                    <>
                                        <Btn
                                            variant="secondary-outlined"
                                            buttonText="+ Add Service Item Section"
                                            buttonOnClick={() => addSection(DS_SECTION_CATALOG.SERVICE_ITEMS)}
                                        />
                                        <Btn
                                            variant="secondary-outlined"
                                            buttonText="+ Add Procedure Section"
                                            buttonOnClick={() => addSection(DS_SECTION_CATALOG.PROCEDURES)}
                                        />
                                    </>
                                ) : (
                                    <button type="button" className="btn-add" onClick={() => addSection()}>
                                        + Add A Section
                                    </button>
                                )}
                            </div>
                            {isDailyService && (
                                <p className="item-section-hint ds-new-issues-note">
                                    New Issues is always the last step on the Daily Service log. Operators
                                    choose Record an issue or No new issues.
                                </p>
                            )}
                        </div>
                    </form>
                </div>

                {/* Footer bar */}
                <CardFooter className="form-footer-bar">
                    <div className="form-footer-bar-stats">
                        <span>
                            {totalItems} item{totalItems !== 1 ? 's' : ''}
                        </span>
                        <span>
                            {totalSections} section{totalSections !== 1 ? 's' : ''}
                        </span>
                        <span>
                            {totalAutoFlag} auto-flag item{totalAutoFlag !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="form-footer-bar-right">
                        <div className="footer-hints">
                            {saveError && (
                                <p className="form-save-error" role="alert">
                                    {saveError}
                                </p>
                            )}
                            {!saveError && formValidationHint && (
                                <p className="form-validation-hint">{formValidationHint}</p>
                            )}
                        </div>
                        <div className="form-footer-actions">
                            {isOverlay && (
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={handleBackClick}
                                    disabled={isCreating}
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn-save-draft"
                                onClick={() => handleSubmit(undefined, 'draft')}
                                disabled={!canSaveDraft}
                                title={getFormValidationMessage('draft') || undefined}
                            >
                                Save Draft
                            </button>
                            {showPublishAction ? (
                                <button
                                    type="button"
                                    className="btn-publish"
                                    disabled={!canPublish}
                                    onClick={() => handleSubmit(undefined, 'published')}
                                    title={formValidationHint || undefined}
                                >
                                    <span className="btn-publish-label">
                                        <span>{isCreating ? 'Saving...' : publishButtonLabel}</span>
                                        <span className="btn-publish-label-sizer" aria-hidden="true">
                                            {publishButtonLabel}
                                        </span>
                                    </span>
                                </button>
                            ) : null}
                        </div>
                    </div>
                </CardFooter>
            </Card>

            {/* Exit Modal */}
            {showExitModal && (
                <CustomModal
                    visible={showExitModal}
                    onDismiss={handleCancelExit}
                    header="Unsaved Changes"
                    footer={
                        <div className="modal-footer-actions">
                            <button type="button" className="btn-cancel" onClick={handleCancelExit}>
                                Cancel
                            </button>
                            <button type="button" className="btn-publish" onClick={handleConfirmExit}>
                                Leave
                            </button>
                        </div>
                    }
                >
                    <p>You have unsaved changes. Are you sure you want to leave?</p>
                </CustomModal>
            )}

            {/* Delete Prompt Modal */}
            {showDeletePromptModal && (
                <CustomModal
                    onDismiss={() => {
                        setShowDeletePromptModal(false);
                        setDeletePromptIndex(null);
                        setDeletePromptInSectionIndex(null);
                    }}
                    visible={showDeletePromptModal}
                    header="Confirm Deletion"
                    footer={
                        <div className="modal-footer-actions">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => {
                                    setShowDeletePromptModal(false);
                                    setDeletePromptIndex(null);
                                    setDeletePromptInSectionIndex(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-publish btn-danger"
                                onClick={() => {
                                    if (deletePromptIndex !== null) {
                                        setFormContents([
                                            ...formContents.slice(0, deletePromptIndex),
                                            ...formContents.slice(deletePromptIndex + 1),
                                        ]);
                                    } else if (deletePromptInSectionIndex !== null) {
                                        const { sectionIndex, promptIndex } = deletePromptInSectionIndex;
                                        const currentSection = formContents[sectionIndex];
                                        if (currentSection?.prompts) {
                                            setFormContents([
                                                ...formContents.slice(0, sectionIndex),
                                                {
                                                    ...currentSection,
                                                    prompts: [
                                                        ...currentSection.prompts.slice(0, promptIndex),
                                                        ...currentSection.prompts.slice(promptIndex + 1),
                                                    ],
                                                },
                                                ...formContents.slice(sectionIndex + 1),
                                            ]);
                                        }
                                    }
                                    setShowDeletePromptModal(false);
                                    setDeletePromptIndex(null);
                                    setDeletePromptInSectionIndex(null);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    }
                >
                    <p>Are you sure you want to delete this item? This action cannot be undone.</p>
                </CustomModal>
            )}

            {/* Delete Section Modal */}
            {showDeleteSectionModal && (
                <CustomModal
                    onDismiss={() => {
                        setShowDeleteSectionModal(false);
                        setDeleteSectionIndex(null);
                    }}
                    visible={showDeleteSectionModal}
                    header="Confirm Section Deletion"
                    footer={
                        <div className="modal-footer-actions">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => {
                                    setShowDeleteSectionModal(false);
                                    setDeleteSectionIndex(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-publish btn-danger"
                                onClick={() => {
                                    if (deleteSectionIndex !== null) {
                                        setFormContents([
                                            ...formContents.slice(0, deleteSectionIndex),
                                            ...formContents.slice(deleteSectionIndex + 1),
                                        ]);
                                    }
                                    setShowDeleteSectionModal(false);
                                    setDeleteSectionIndex(null);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    }
                >
                    <p>
                        Are you sure you want to delete this entire section and all its items? This action cannot be
                        undone.
                    </p>
                </CustomModal>
            )}

            {/* Unsupported Question Types Warning */}
            {showUnsupportedWarning && (
                <CustomModal
                    onDismiss={() => setShowUnsupportedWarning(false)}
                    visible={showUnsupportedWarning}
                    header="New Question Types Not Yet Supported"
                    footer={
                        <div className="modal-footer-actions">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => setShowUnsupportedWarning(false)}
                            >
                                Go Back
                            </button>
                            <button
                                type="button"
                                className="btn-publish"
                                onClick={() => handleSubmit(undefined, 'published')}
                            >
                                Save Anyway
                            </button>
                        </div>
                    }
                >
                    <div className="unsupported-warning-content">
                        <p>
                            This form contains <strong>{unsupportedCount}</strong> question
                            {unsupportedCount !== 1 ? 's' : ''} using new response types (Added/Not Added,
                            OK/Cleaned/Replaced/N/A, OK/Replaced/N/A, or Quantity Only).
                        </p>
                        <p>
                            These question types are <strong>not yet supported</strong> on the inspector fill-out
                            screen. They will be <strong>skipped during inspection</strong> until the inspection screen
                            is updated.
                        </p>
                        <p>
                            You can still save the form — the questions will be preserved and will work once support is
                            added.
                        </p>
                    </div>
                </CustomModal>
            )}

            {/* Vendor Modal (reusing existing VendorModal from company-resources) */}
            {showVendorModal && companyId && (
                <VendorModal
                    isOpen={showVendorModal}
                    onClose={() => {
                        setShowVendorModal(false);
                        setVendorPromptTarget(null);
                        setVendorDraftName('');
                    }}
                    onSave={async () => {
                        // Refresh vendors and set the newly created one on the target prompt
                        const updatedVendors = await listVendorsByCompanyId(companyId);
                        setVendors(updatedVendors);
                        // Find the newest vendor (highest updatedAt) and assign it
                        if (vendorPromptTarget && updatedVendors.length > 0) {
                            const sorted = [...updatedVendors].sort(
                                (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime(),
                            );
                            const newest = sorted[0];
                            updatePromptField(
                                vendorPromptTarget.sectionIndex,
                                vendorPromptTarget.promptIndex,
                                'vendor',
                                newest.name,
                            );
                            const prompt =
                                formContents[vendorPromptTarget.sectionIndex]?.prompts?.[
                                    vendorPromptTarget.promptIndex
                                ];
                            if (prompt?.serviceItemId) {
                                void persistPromptListingToCatalog(
                                    { ...prompt, vendor: newest.name },
                                    (formContents[vendorPromptTarget.sectionIndex] as tSectionContent | undefined)
                                        ?.name || '',
                                    updatedVendors,
                                    { vendorName: newest.name },
                                ).then((item) => {
                                    if (item) {
                                        applyCatalogItemToPrompt(
                                            vendorPromptTarget.sectionIndex,
                                            vendorPromptTarget.promptIndex,
                                            item,
                                        );
                                    }
                                });
                            }
                        }
                        setShowVendorModal(false);
                        setVendorPromptTarget(null);
                        setVendorDraftName('');
                    }}
                    editVendor={null}
                    initialName={vendorDraftName}
                    companyId={companyId}
                />
            )}
            <EquipmentCategoryFormOverlay
                visible={showCategoryOverlay}
                defaultCategoryName={pendingCategorySearchText}
                onDismiss={() => {
                    setShowCategoryOverlay(false);
                    setPendingCategorySearchText('');
                }}
                onCategoryCreated={(category) => {
                    setEquipmentType(category.name || '');
                    setShowCategoryOverlay(false);
                    setPendingCategorySearchText('');
                }}
            />
            {procedureModalTarget && (
                <ServiceProcedureModal
                    isOpen
                    initialName={procedureModalTarget.initialName}
                    onClose={() => setProcedureModalTarget(null)}
                    onSave={async (saved) => {
                        const procedures = await listServiceProceduresByCompanyId(companyId);
                        setServiceProcedures(procedures);
                        const created =
                            saved ||
                            procedures.find(
                                (entry) =>
                                    entry.itemName.trim().toLowerCase() ===
                                    procedureModalTarget.initialName.trim().toLowerCase(),
                            );
                        if (created) {
                            bindProcedureToPrompt(
                                procedureModalTarget.sectionIndex,
                                procedureModalTarget.promptIndex,
                                created,
                            );
                        }
                        setProcedureModalTarget(null);
                    }}
                    editItem={null}
                    categories={[...new Set(serviceProcedures.map((entry) => entry.category).filter(Boolean))]}
                    allItems={serviceProcedures}
                    companyId={companyId}
                />
            )}
        </div>
    );
};
